// SupplierList.jsx - Complete with Hardcoded Bilingual UI & Vector-based PDF Export
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useAuth } from '../module_roots/AuthContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import "./Styles/FormList.css";
import "./Styles/NativeMobile.css";
import "../styles/LanguageTypography.css";
import {
    faEdit, faTrash, faSave, faSearch, faRefresh, faSpinner,
    faExclamationTriangle, faCheckCircle, faTimes, faChevronLeft,
    faChevronRight, faFilePdf, faCalendar, faUser, faStore, faPhone,
    faEnvelope, faMapMarkerAlt, faTag, faExchangeAlt, faChevronDown,
    faExpand, faCompress, faArrowsAlt, faMinus, faPlus, faFilter, faEye, faEyeSlash,
    faGlobe, faUserTag, faStickyNote
} from '@fortawesome/free-solid-svg-icons';
import jsPDF from 'jspdf';


// Resolve CommonJS, default and named exports across AutoTable 3/4/5.
const autoTableModule = require('jspdf-autotable');
const drawPdfTable = (doc, options) => {
    const candidates = [autoTableModule, autoTableModule.autoTable,
        autoTableModule.default, autoTableModule.default?.autoTable];
    const draw = candidates.find(candidate => typeof candidate === 'function');
    if (draw) return draw(doc, options);
    if (typeof doc.autoTable === 'function') return doc.autoTable(options);
    throw new Error('Unsupported jspdf-autotable installation. Install jspdf-autotable 3, 4 or 5.');
};


// HarfBuzz + Unicode bidi layout for Noori Nastaliq, drawn as PDF vector paths.
// Do not pre-reshape, reverse, letter-space, or justify the input strings.
const hasUrdu = value => /[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\uFB50-\uFDFF\uFE70-\uFEFF]/.test(String(value ?? ''));

const scriptPromises = new Map();
let enginePromise;
const publicBase = () => (process.env.PUBLIC_URL || '').replace(/\/$/, '');

function loadScript(file, globalName) {
    if (typeof window[globalName] === 'function') return Promise.resolve();
    const url = `${publicBase()}/pdf-engine/${file}`;
    if (!scriptPromises.has(url)) {
        scriptPromises.set(url, new Promise((resolve, reject) => {
            const script = document.createElement('script');
            script.src = url;
            script.async = true;
            const timer = setTimeout(() => fail(), 30000);
            const fail = () => {
                clearTimeout(timer);
                script.remove();
                scriptPromises.delete(url);
                reject(new Error(`Could not load ${url}. Copy the supplied public/pdf-engine folder.`));
            };
            script.onload = () => {
                clearTimeout(timer);
                if (typeof window[globalName] !== 'function') return fail();
                resolve();
            };
            script.onerror = fail;
            document.head.appendChild(script);
        }));
    }
    return scriptPromises.get(url);
}

async function getEngine() {
    if (!enginePromise) {
        enginePromise = (async () => {
            await Promise.all([
                loadScript('hb.js', 'createHarfBuzz'),
                loadScript('hbjs.js', 'hbjs'),
                loadScript('bidi.min.js', 'bidi_js')
            ]);
            const url = `${publicBase()}/fonts/JameelNooriNastaleeq.ttf`;
            const response = await fetch(url);
            if (!response.ok) throw new Error(`Noori font download failed (${response.status}): ${url}`);
            const bytes = await response.arrayBuffer();
            const signature = new DataView(bytes).getUint32(0);
            if (signature !== 0x00010000 && signature !== 0x4f54544f && signature !== 0x74727565) {
                throw new Error('JameelNooriNastaleeq.ttf is not a font. Copy the supplied public/fonts folder.');
            }
            const module = await window.createHarfBuzz({
                locateFile: file => `${publicBase()}/pdf-engine/${file}`
            });
            const hb = window.hbjs(module);
            const blob = hb.createBlob(bytes);
            const face = hb.createFace(blob, 0);
            const font = hb.createFont(face);
            if (!face.upem) throw new Error('Noori font could not be opened.');
            font.setScale(face.upem, face.upem);
            return { hb, font, face, blob, bidi: window.bidi_js() };
        })().catch(error => {
            enginePromise = null;
            throw error;
        });
    }
    return enginePromise;
}

async function createNooriPdfText(doc, needsUrdu = true) {
    const engine = needsUrdu ? await getEngine() : null;
    const paths = new Map();
    const lineCache = new Map();
    const factor = doc.internal.scaleFactor;
    const normalize = value => String(value ?? '').normalize('NFKC').replace(/\r\n?/g, '\n').replace(/\t/g, ' ');
    const baseDirection = text => {
        if (!engine) return 'ltr';
        return engine.bidi.getEmbeddingLevels(text).paragraphs[0]?.level % 2 ? 'rtl' : 'ltr';
    };
    const glyphPath = id => {
        if (!paths.has(id)) paths.set(id, engine.font.glyphToJson(id));
        return paths.get(id);
    };
    const plainWidth = (text, size, bold) => {
        doc.setFont('helvetica', bold ? 'bold' : 'normal');
        doc.setFontSize(size);
        return doc.getTextWidth(text);
    };

    function shapeLine(text, size, bold, direction, latinSize) {
        const key = JSON.stringify([text, size, bold, direction, latinSize]);
        if (lineCache.has(key)) return lineCache.get(key);
        const em = size / factor;
        let logicalRuns = [{ text, level: 0, start: 0, end: text.length }];
        let visualRuns = logicalRuns;
        if (engine && text) {
            const embedding = engine.bidi.getEmbeddingLevels(text, direction);
            logicalRuns = [];
            for (let start = 0; start < text.length;) {
                let end = start + 1;
                while (end < text.length && embedding.levels[end] === embedding.levels[start]) end++;
                logicalRuns.push({ text: text.slice(start, end), level: embedding.levels[start], start, end });
                start = end;
            }
            const indices = engine.bidi.getReorderedIndices(text, embedding);
            const owner = new Array(text.length);
            logicalRuns.forEach((run, id) => owner.fill(id, run.start, run.end));
            const visited = new Set();
            visualRuns = [];
            indices.forEach(index => {
                const id = owner[index];
                if (!visited.has(id)) { visited.add(id); visualRuns.push(logicalRuns[id]); }
            });
        }
        let cursor = 0;
        let minX = 0;
        let maxX = 0;
        let minY = -em * 0.78;
        let maxY = em * 0.22;
        const runs = [];
        for (const run of visualRuns) {
            if (hasUrdu(run.text)) {
                if (!engine) throw new Error('Urdu text needs the Noori font engine.');
                const buffer = engine.hb.createBuffer();
                let glyphs;
                try {
                    buffer.addText(run.text);
                    buffer.guessSegmentProperties();
                    buffer.setDirection(run.level % 2 ? 'rtl' : 'ltr');
                    buffer.setLanguage('ur');
                    buffer.setScript('Arab');
                    engine.hb.shape(engine.font, buffer);
                    glyphs = buffer.json();
                } finally { buffer.destroy(); }
                const scale = em / engine.face.upem;
                let penX = 0;
                let penY = 0;
                const positioned = glyphs.map(glyph => {
                    if (glyph.g === 0) throw new Error(`Noori font has an unsupported character in: ${run.text}`);
                    const x = cursor + (penX + glyph.dx) * scale;
                    const y = -(penY + glyph.dy) * scale;
                    const path = glyphPath(glyph.g);
                    path.forEach(command => {
                        for (let i = 0; i < command.values.length; i += 2) {
                            const px = x + command.values[i] * scale;
                            const py = y - command.values[i + 1] * scale;
                            minX = Math.min(minX, px); maxX = Math.max(maxX, px);
                            minY = Math.min(minY, py); maxY = Math.max(maxY, py);
                        }
                    });
                    penX += glyph.ax;
                    penY += glyph.ay;
                    return { x, y, path, scale };
                });
                runs.push({ glyphs: positioned });
                cursor += penX * scale;
            } else {
                let visibleText = run.text.replace(/[\u200e\u200f\u202a-\u202e\u2066-\u2069]/g, '');
                if (engine && run.level % 2) {
                    visibleText = Array.from(visibleText).reverse()
                        .map(c => engine.bidi.getMirroredCharacter(c) || c).join('');
                }
                runs.push({ text: visibleText, x: cursor });
                cursor += plainWidth(visibleText, latinSize, bold);
            }
            maxX = Math.max(maxX, cursor);
        }
        const line = { text, runs, size, latinSize, bold, minX, minY, width: maxX - minX, height: maxY - minY };
        if (lineCache.size >= 3000) lineCache.clear();
        lineCache.set(key, line);
        return line;
    }

    const blockFromLines = (lines, gap) => ({
        lines, gap,
        width: Math.max(0, ...lines.map(line => line.width)),
        height: lines.reduce((sum, line) => sum + line.height, 0) + Math.max(0, lines.length - 1) * gap
    });

    function layout(value, { size = 10, maxWidth = Infinity, bold = false, direction } = {}) {
        const text = normalize(value);
        const latinSize = hasUrdu(text) ? size * 0.7 : size;
        const lines = [];
        if (!(maxWidth > 0)) throw new Error('PDF text box must have a positive width.');
        const graphemes = word => typeof Intl.Segmenter === 'function'
            ? Array.from(new Intl.Segmenter('ur', { granularity: 'grapheme' }).segment(word), s => s.segment)
            : Array.from(word);
        for (const paragraph of text.split('\n')) {
            const dir = direction || baseDirection(paragraph);
            const measure = value => shapeLine(value, size, bold, dir, latinSize);
            if (!paragraph) { lines.push(measure('')); continue; }
            let current = '';
            const flush = () => {
                if (current) lines.push(measure(current.trimEnd()));
                current = '';
            };
            // Wrap logical words first, then shape each final line from scratch.
            // Never split already-shaped glyph strings or ligatures.
            for (const token of paragraph.match(/\S+\s*|\s+/g) || []) {
                const candidate = current + token;
                if (measure(candidate.trimEnd()).width <= maxWidth) { current = candidate; continue; }
                flush();
                const word = token.trimStart();
                if (measure(word.trimEnd()).width <= maxWidth) { current = word; continue; }
                for (const cluster of graphemes(word)) {
                    if (current && measure(current + cluster).width > maxWidth) flush();
                    current += cluster;
                }
            }
            flush();
        }
        // A single unusually wide grapheme is scaled uniformly, never clipped.
        const fitted = lines.map(line => line.width > maxWidth
            ? { ...line, fitScale: maxWidth / line.width, width: maxWidth,
                height: line.height * maxWidth / line.width }
            : line);
        return blockFromLines(fitted, size / factor * 0.2);
    }

    function drawGlyph(glyph, originX, baseline, fit) {
        const commands = [];
        let currentX = 0; let currentY = 0;
        let startX = 0; let startY = 0;
        const point = (x, y) => [originX + (glyph.x + x * glyph.scale) * fit,
            baseline + (glyph.y - y * glyph.scale) * fit];
        for (const command of glyph.path) {
            const v = command.values;
            if (command.type === 'M' || command.type === 'L') {
                const p = point(v[0], v[1]);
                commands.push({ op: command.type.toLowerCase(), c: p });
                [currentX, currentY] = p;
                if (command.type === 'M') { startX = currentX; startY = currentY; }
            } else if (command.type === 'Q') {
                const control = point(v[0], v[1]);
                const end = point(v[2], v[3]);
                commands.push({ op: 'c', c: [
                    currentX + 2 / 3 * (control[0] - currentX), currentY + 2 / 3 * (control[1] - currentY),
                    end[0] + 2 / 3 * (control[0] - end[0]), end[1] + 2 / 3 * (control[1] - end[1]), ...end
                ] });
                [currentX, currentY] = end;
            } else if (command.type === 'C') {
                const end = point(v[4], v[5]);
                commands.push({ op: 'c', c: [...point(v[0], v[1]), ...point(v[2], v[3]), ...end] });
                [currentX, currentY] = end;
            } else if (command.type === 'Z') {
                commands.push({ op: 'h', c: [] });
                currentX = startX; currentY = startY;
            }
        }
        if (commands.length) doc.path(commands).fill();
    }

    function draw(block, x, top, width = block.width, align = 'left', color = [30, 41, 59]) {
        doc.saveGraphicsState();
        doc.setTextColor(...color);
        doc.setFillColor(...color);
        let y = top;
        for (const line of block.lines) {
            const fit = line.fitScale || 1;
            const left = x + (align === 'center' ? (width - line.width) / 2
                : align === 'right' ? width - line.width : 0);
            const origin = left - line.minX * fit;
            const baseline = y - line.minY * fit;
            for (const run of line.runs) {
                if (run.glyphs) run.glyphs.forEach(glyph => drawGlyph(glyph, origin, baseline, fit));
                else if (run.text) {
                    doc.setFont('helvetica', line.bold ? 'bold' : 'normal');
                    doc.setFontSize(line.latinSize * fit);
                    doc.text(run.text, origin + run.x * fit, baseline, {
                        isInputVisual: true, isOutputVisual: true, isInputRtl: false, isOutputRtl: false
                    });
                }
            }
            y += line.height + block.gap;
        }
        doc.restoreGraphicsState();
    }

    function split(block, maximumHeight) {
        const chunks = [];
        let lines = [];
        let height = 0;
        for (const line of block.lines) {
            if (line.height > maximumHeight) throw new Error('A text line is too tall for the selected page.');
            const extra = line.height + (lines.length ? block.gap : 0);
            if (lines.length && height + extra > maximumHeight) {
                chunks.push(blockFromLines(lines, block.gap)); lines = []; height = 0;
            }
            height += line.height + (lines.length ? block.gap : 0);
            lines.push(line);
        }
        if (lines.length) chunks.push(blockFromLines(lines, block.gap));
        return chunks.length ? chunks : [blockFromLines([], block.gap)];
    }
    return { layout, draw, split, baseDirection };
}


const SUPPLIERS_API = 'https://ourzestapp.online/oz_backend/mechanics_module/mechanics_suppliers.php';

// ============================================================
// ONLY ENGLISH AND URDU LANGUAGES
// ============================================================
const RTL_LANGUAGES = new Set(['ur']);
const SUPPORTED_LANGUAGES = new Set(['en', 'ur']);

const normalizeLanguage = (lang) => {
    if (!lang) return 'en';
    const normalized = lang.toLowerCase().split('-')[0];
    return SUPPORTED_LANGUAGES.has(normalized) ? normalized : 'en';
};

// ============================================================
// HARDCODED BILINGUAL UI DICTIONARY - ENGLISH & URDU ONLY
// ============================================================
const UI = {
    en: {
        supplierList: 'Suppliers List',
        formDetails: 'All suppliers details are below',
        suppliers: 'Suppliers',
        shopName: 'Shop',
        noShopSelected: 'No shop selected. Please select a shop first.',
        switchShop: 'Switch Shop',
        loadingShops: 'Loading shops...',
        noShops: 'No shops available',
        current: 'Current',
        searchPlaceholder: 'Search suppliers...',
        refresh: 'Refresh',
        loadingUserData: 'Loading your supplier data...',
        selectShop: 'Please select a shop to view suppliers',
        supplierName: 'Supplier Name',
        company: 'Company',
        phone: 'Phone',
        email: 'Email',
        date: 'Date',
        category: 'Category',
        actions: 'Actions',
        addedBy: 'Added By',
        edit: 'Edit',
        view: 'View',
        delete: 'Delete',
        serialNo: 'S.No',
        showing: 'Showing',
        of: 'of',
        previous: 'Previous',
        next: 'Next',
        all: 'All',
        supplierNameFilter: 'Supplier Name',
        companyFilter: 'Company Name',
        phoneFilter: 'Phone Number',
        emailFilter: 'Email',
        categoryFilter: 'Category',
        dateRange: 'Date Range',
        addedByFilter: 'Added By',
        startDate: 'Start Date',
        endDate: 'End Date',
        exportToPDF: 'Export PDF',
        orientation: 'Orientation',
        portrait: 'Portrait',
        landscape: 'Landscape',
        pageSize: 'Page Size',
        a4: 'A4',
        letter: 'Letter',
        legal: 'Legal',
        a5: 'A5',
        supplierDetails: 'Supplier Details',
        contactInfo: 'Contact Information',
        addressInfo: 'Address Information',
        editSupplier: 'Edit Supplier',
        saveChanges: 'Save Changes',
        cancel: 'Cancel',
        confirmDelete: 'Confirm Delete',
        deleteMessage: 'Are you sure you want to delete this supplier?',
        deleteWarning: 'This action cannot be undone.',
        yesDelete: 'Yes, Delete',
        noCancel: 'No, Cancel',
        success: 'Success',
        error: 'Error',
        warning: 'Warning',
        close: 'Close',
        supplierReport: 'Supplier Report',
        supplierDirectory: 'Supplier Directory',
        totalSuppliers: 'Total Suppliers',
        generated: 'Generated',
        reportId: 'Report ID',
        generatedBy: 'Generated by',
        shopCode: 'Shop Code',
        filtersApplied: 'FILTERS APPLIED',
        search: 'Search',
        from: 'From',
        to: 'To',
        page: 'Page',
        noDataToExport: 'No data to export',
        supplierNameRequired: 'Supplier name is required',
        invalidPhone: 'Please enter a valid phone number',
        invalidEmail: 'Please enter a valid email address',
        supplierUpdated: 'Supplier updated successfully',
        updateError: 'Error updating supplier',
        deletedSuccess: 'Supplier deleted successfully',
        deleteError: 'Error deleting supplier',
        networkError: 'Network error occurred',
        dateAdded: 'Date Added',
        lastUpdated: 'Last Updated',
        address: 'Address',
        selectCategory: 'Select Category',
        optional: 'Optional',
        reportGenerated: 'Report generated!',
        on: 'on',
        pages: 'page(s)',
        pdfGenerationError: 'Error generating PDF',
        filterBy: 'Filter By',
        allCategories: 'All Categories',
        regular: 'Regular',
        vip: 'VIP',
        other: 'Other',
        noOptions: 'No options found',
        allPages: 'All Pages',
        pageRange: 'Page Range',
        hideFilters: 'Hide Filters',
        showFilters: 'Show Filters',
        filters: 'Filters',
        supplierSummary: 'Supplier Summary',
        system: 'System',
        notAvailable: 'N/A',
        reportFooter: 'Generated with OurZest',
        updating: 'Updating...',
        unnamed: 'Unnamed',
        noSuppliers: 'No suppliers found',
        shopSwitched: 'Shop switched successfully',
        shopSwitchFailed: 'Failed to switch shop',
        noCategoriesFound: 'No categories found',
        website: 'Website',
        contactPerson: 'Contact Person',
        notes: 'Notes',
    },
    ur: {
        supplierList: 'سپلائرز کی فہرست',
        formDetails: 'تمام سپلائرز کی تفصیلات ذیل میں ہیں',
        suppliers: 'سپلائرز',
        shopName: 'دکان',
        noShopSelected: 'کوئی دکان منتخب نہیں۔ براہ کرم پہلے دکان منتخب کریں۔',
        switchShop: 'دکان تبدیل کریں',
        loadingShops: 'دکانیں لوڈ ہو رہی ہیں...',
        noShops: 'کوئی دکان دستیاب نہیں',
        current: 'موجودہ',
        searchPlaceholder: 'سپلائرز تلاش کریں...',
        refresh: 'ریفریش',
        loadingUserData: 'آپ کا ڈیٹا لوڈ ہو رہا ہے...',
        selectShop: 'براہ کرم سپلائرز دیکھنے کے لیے دکان منتخب کریں',
        supplierName: 'سپلائر کا نام',
        company: 'کمپنی',
        phone: 'فون نمبر',
        email: 'ای میل',
        date: 'تاریخ',
        category: 'کیٹیگری',
        actions: 'ایکشنز',
        addedBy: 'ایڈ کردہ',
        edit: 'ایڈٹ',
        view: 'دیکھیں',
        delete: 'ڈیلیٹ',
        serialNo: 'شمار',
        showing: 'دکھا رہا ہے',
        of: 'میں سے',
        previous: 'پچھلا',
        next: 'اگلا',
        all: 'سب',
        supplierNameFilter: 'سپلائر کا نام',
        companyFilter: 'کمپنی کا نام',
        phoneFilter: 'فون نمبر',
        emailFilter: 'ای میل',
        categoryFilter: 'کیٹیگری',
        dateRange: 'تاریخ کی حد',
        addedByFilter: 'ایڈ کردہ',
        startDate: 'شروع کی تاریخ',
        endDate: 'آخری تاریخ',
        exportToPDF: 'پی ڈی ایف ایکسپورٹ',
        orientation: 'اورینٹیشن',
        portrait: 'عمودی',
        landscape: 'افقی',
        pageSize: 'صفحہ سائز',
        a4: 'A4',
        letter: 'لیٹر',
        legal: 'لیگل',
        a5: 'A5',
        supplierDetails: 'سپلائر کی تفصیلات',
        contactInfo: 'رابطہ کی معلومات',
        addressInfo: 'پتے کی معلومات',
        editSupplier: 'سپلائر میں ایڈٹ',
        saveChanges: 'تبدیلیاں محفوظ کریں',
        cancel: 'منسوخ',
        confirmDelete: 'ڈیلیٹ کی تصدیق',
        deleteMessage: 'کیا آپ اس سپلائر کو ڈیلیٹ کرنا چاہتے ہیں؟',
        deleteWarning: 'یہ عمل واپس نہیں کیا جا سکتا۔',
        yesDelete: 'ہاں، ڈیلیٹ کریں',
        noCancel: 'نہیں، منسوخ',
        success: 'کامیابی',
        error: 'خرابی',
        warning: 'انتباہ',
        close: 'بند کریں',
        supplierReport: 'سپلائر رپورٹ',
        supplierDirectory: 'سپلائر ڈائریکٹری',
        totalSuppliers: 'کل سپلائرز',
        generated: 'تیار کیا گیا',
        reportId: 'رپورٹ آئی ڈی',
        generatedBy: 'تیار کردہ',
        shopCode: 'دکان کوڈ',
        filtersApplied: 'فلٹرز لگائے گئے',
        search: 'تلاش',
        from: 'سے',
        to: 'تک',
        page: 'صفحہ',
        noDataToExport: 'ایکسپورٹ کرنے کے لیے کوئی ڈیٹا نہیں',
        supplierNameRequired: 'سپلائر کا نام ضروری ہے',
        invalidPhone: 'براہ کرم درست فون نمبر درج کریں',
        invalidEmail: 'براہ کرم درست ای میل پتہ درج کریں',
        supplierUpdated: 'سپلائر کامیابی سے اپ ڈیٹ ہو گیا',
        updateError: 'سپلائر اپ ڈیٹ کرنے میں خرابی',
        deletedSuccess: 'سپلائر کامیابی سے ڈیلیٹ ہو گیا',
        deleteError: 'سپلائر ڈیلیٹ کرنے میں خرابی',
        networkError: 'نیٹ ورک کی خرابی',
        dateAdded: 'شامل کرنے کی تاریخ',
        lastUpdated: 'آخری اپ ڈیٹ',
        address: 'پتہ',
        selectCategory: 'کیٹیگری منتخب کریں',
        optional: '',
        reportGenerated: 'رپورٹ تیار ہو گئی',
        on: 'پر',
        pages: 'صفحات',
        pdfGenerationError: 'پی ڈی ایف بنانے میں خرابی',
        filterBy: 'فلٹر کریں',
        allCategories: 'تمام کیٹیگریز',
        regular: 'ریگولر',
        vip: 'وی آئی پی',
        other: 'دیگر',
        noOptions: 'کوئی آپشن نہیں',
        allPages: 'تمام صفحات',
        pageRange: 'صفحات کی حد',
        hideFilters: 'فلٹرز چھپائیں',
        showFilters: 'فلٹرز دکھائیں',
        filters: 'فلٹرز',
        supplierSummary: 'سپلائر کا خلاصہ',
        system: 'سسٹم',
        notAvailable: 'دستیاب نہیں',
        reportFooter: 'آور زیسٹ کے ذریعے تیار کردہ',
        updating: 'اپ ڈیٹ ہو رہا ہے...',
        unnamed: 'بے نام',
        noSuppliers: 'کوئی سپلائر نہیں ملا',
        shopSwitched: 'دکان کامیابی سے تبدیل ہو گئی',
        shopSwitchFailed: 'دکان تبدیل نہیں ہو سکی',
        noCategoriesFound: 'کوئی کیٹیگری نہیں ملی',
        website: 'ویب سائٹ',
        contactPerson: 'رابطہ شخص',
        notes: 'نوٹس',
    }
};

// ============================================================
// TOAST NOTIFICATION - POSITION BASED ON LANGUAGE
// ============================================================
const ToastNotification = ({ toast, setToast, t, isRTL }) => {
    const getIconForType = (type) => {
        switch (type) {
            case 'success': return faCheckCircle;
            case 'error': return faExclamationTriangle;
            case 'warning': return faExclamationTriangle;
            default: return faCheckCircle;
        }
    };

    const getTitleForType = (type) => {
        switch (type) {
            case 'success': return t.success || 'Success';
            case 'error': return t.error || 'Error';
            case 'warning': return t.warning || 'Warning';
            default: return '';
        }
    };

    if (!toast.show) return null;

    const positionStyle = isRTL
        ? { bottom: '20px', left: '20px', right: 'auto' }
        : { bottom: '20px', right: '20px', left: 'auto' };

    return (
        <div className={`ozlistui-toast-notification ${toast.type}`} style={positionStyle}>
            <div className="ozlistui-toast-content">
                <div className="ozlistui-toast-icon">
                    <FontAwesomeIcon icon={getIconForType(toast.type)} />
                </div>
                <div className="ozlistui-toast-text">
                    <div className="ozlistui-toast-title">{getTitleForType(toast.type)}</div>
                    <div className="ozlistui-toast-message">{toast.message}</div>
                </div>
                <button className="ozlistui-toast-close" onClick={() => setToast(prev => ({ ...prev, show: false }))}>
                    <FontAwesomeIcon icon={faTimes} />
                </button>
            </div>
        </div>
    );
};

// ============================================================
// SHOP SELECTOR MODAL
// ============================================================
const ShopSelectorModal = ({ isOpen, onClose, shops, currentShop, onSelectShop, t, loading, isRTL }) => {
    if (!isOpen) return null;

    return (
        <div className="ozlistui-modal-overlay" onClick={onClose}>
            <div className="ozlistui-modal-content" onClick={(e) => e.stopPropagation()} style={{ direction: isRTL ? 'rtl' : 'ltr' }}>
                <div className="ozlistui-modal-header">
                    <h3>{t.switchShop}</h3>
                    <button className="ozlistui-close-btn" onClick={onClose}>
                        <FontAwesomeIcon icon={faTimes} />
                    </button>
                </div>
                <div className="ozlistui-modal-body">
                    {loading ? (
                        <div className="ozlistui-loading-shops">
                            <div className="ozlistui-loading-spinner-small"></div>
                            <p>{t.loadingShops}</p>
                        </div>
                    ) : shops.length === 0 ? (
                        <div className="ozlistui-no-shops">
                            <p>{t.noShops}</p>
                        </div>
                    ) : (
                        <div className="ozlistui-shop-list">
                            {shops.map((shop) => (
                                <div
                                    key={shop.shop_id}
                                    className={`ozlistui-shop-item ${currentShop?.shop_id === shop.shop_id ? 'ozlistui-active' : ''}`}
                                    onClick={() => onSelectShop(shop)}
                                >
                                    <div className="ozlistui-shop-item-icon">
                                        <FontAwesomeIcon icon={faStore} />
                                    </div>
                                    <div className="ozlistui-shop-item-details">
                                        <div className="ozlistui-shop-item-name">{shop.shop_name}</div>
                                        <div className="ozlistui-shop-item-code">{shop.shop_code || `ID: ${shop.shop_id}`}</div>
                                    </div>
                                    {currentShop?.shop_id === shop.shop_id && (
                                        <div className="ozlistui-shop-item-current">
                                            <FontAwesomeIcon icon={faCheckCircle} />
                                            <span>{t.current}</span>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

// ============================================================
// CUSTOM DROPDOWN COMPONENT
// ============================================================
const CustomDropdown = ({ label, icon, value, onChange, options, t, disabled = false, isRTL = false }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [highlightedIndex, setHighlightedIndex] = useState(-1);
    const wrapperRef = useRef(null);
    const menuRef = useRef(null);

    const selectedOption = options.find(opt => opt.value === value);
    const displayValue = selectedOption?.label || '';

    useEffect(() => {
        setSearchTerm(displayValue);
    }, [displayValue]);

    const filteredOptions = searchTerm.trim() === ''
        ? options
        : options.filter(opt =>
            opt.label.toLowerCase().includes(searchTerm.toLowerCase())
        );

    const handleSelect = (option) => {
        onChange(option.value);
        setSearchTerm(option.label);
        setIsOpen(false);
        setHighlightedIndex(-1);
    };

    const handleKeyDown = (e) => {
        if (!isOpen) {
            if (e.key === 'ArrowDown' || e.key === 'ArrowUp' || e.key === 'Enter') {
                e.preventDefault();
                setIsOpen(true);
                setHighlightedIndex(0);
            }
            return;
        }

        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault();
                setHighlightedIndex(prev => prev < filteredOptions.length - 1 ? prev + 1 : prev);
                break;
            case 'ArrowUp':
                e.preventDefault();
                setHighlightedIndex(prev => prev > 0 ? prev - 1 : -1);
                break;
            case 'Enter':
                e.preventDefault();
                if (highlightedIndex >= 0 && highlightedIndex < filteredOptions.length) {
                    handleSelect(filteredOptions[highlightedIndex]);
                }
                break;
            case 'Escape':
                e.preventDefault();
                setIsOpen(false);
                setHighlightedIndex(-1);
                break;
            default:
                break;
        }
    };

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
                setIsOpen(false);
                setHighlightedIndex(-1);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    useEffect(() => {
        if (highlightedIndex >= 0 && menuRef.current) {
            const items = menuRef.current.querySelectorAll('.ozlistui-custom-dropdown-option');
            if (items[highlightedIndex]) {
                items[highlightedIndex].scrollIntoView({ block: 'nearest' });
            }
        }
    }, [highlightedIndex]);

    return (
        <div className="ozlistui-custom-dropdown-wrapper" ref={wrapperRef}>
            <div className="ozlistui-custom-input-wrapper">
                <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => {
                        setSearchTerm(e.target.value);
                        setIsOpen(true);
                        setHighlightedIndex(0);
                        if (!options.find(opt => opt.label.toLowerCase() === e.target.value.toLowerCase())) {
                            onChange('');
                        }
                    }}
                    onClick={() => {
                        if (!disabled) {
                            setSearchTerm(displayValue);
                            setIsOpen(true);
                            setHighlightedIndex(0);
                        }
                    }}
                    onFocus={() => {
                        if (!disabled) {
                            setSearchTerm(displayValue);
                            setIsOpen(true);
                            setHighlightedIndex(0);
                        }
                    }}
                    onKeyDown={handleKeyDown}
                    placeholder=" "
                    disabled={disabled}
                    autoComplete="off"
                    dir={isRTL ? 'rtl' : 'ltr'}
                />
                <label>
                    {icon && <FontAwesomeIcon icon={icon} />}
                    {label}
                    {!isRTL && t.optional && <span> ({t.optional})</span>}
                </label>
                <FontAwesomeIcon icon={faChevronDown} className="ozlistui-custom-dropdown-arrow" />
            </div>

            {isOpen && (
                <div className="ozlistui-custom-dropdown-menu" ref={menuRef} style={{ direction: isRTL ? 'rtl' : 'ltr' }}>
                    {filteredOptions.length > 0 ? (
                        filteredOptions.map((opt, index) => (
                            <div
                                key={opt.value}
                                className={`ozlistui-custom-dropdown-option ${value === opt.value ? 'ozlistui-selected' : ''} ${highlightedIndex === index ? 'ozlistui-highlighted' : ''}`}
                                onClick={() => handleSelect(opt)}
                                onMouseEnter={() => setHighlightedIndex(index)}
                            >
                                {opt.label}
                            </div>
                        ))
                    ) : (
                        <div className="ozlistui-custom-dropdown-empty">
                            {t?.noOptions || 'No options found'}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

// ============================================================
// MAIN COMPONENT
// ============================================================
const SupplierList = () => {
    const auth = useAuth();
    const { user, currentShop, switchShop, getShopsForSwitch } = auth;

    // ============================================================
    // LANGUAGE DETECTION
    // ============================================================
    const [currentLanguage, setCurrentLanguage] = useState(() => {
        return normalizeLanguage(
            auth?.currentLanguage ||
            auth?.language ||
            user?.language ||
            user?.preferred_language ||
            localStorage.getItem('ourzest_language') ||
            'en'
        );
    });

    const isRTL = currentLanguage === 'ur';
    const t = UI[currentLanguage] || UI.en;

    const [suppliers, setSuppliers] = useState([]);
    const [filteredSuppliers, setFilteredSuppliers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedSupplier, setSelectedSupplier] = useState(null);
    const [showViewModal, setShowViewModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [supplierToDelete, setSupplierToDelete] = useState(null);
    const [editLoading, setEditLoading] = useState(false);
    const [showFilters, setShowFilters] = useState(false);

    // PDF Options
    const [pdfOrientation, setPdfOrientation] = useState('landscape');
    const [pdfPageSize, setPdfPageSize] = useState('a4');
    const [pdfPageRange, setPdfPageRange] = useState('all');
    const [pdfStartPage, setPdfStartPage] = useState(1);
    const [pdfEndPage, setPdfEndPage] = useState(1);
    const [pdfLoading, setPdfLoading] = useState(false);

    // Shop selector state
    const [showShopSelector, setShowShopSelector] = useState(false);
    const [availableShops, setAvailableShops] = useState([]);
    const [loadingShops, setLoadingShops] = useState(false);

    // Modal drag and resize state
    const [modalPosition, setModalPosition] = useState({ x: 0, y: 0 });
    const [modalSize, setModalSize] = useState({ width: 650, height: 'auto' });
    const [isDragging, setIsDragging] = useState(false);
    const [isResizing, setIsResizing] = useState(false);
    const [currentWidthMode, setCurrentWidthMode] = useState('wide');
    const [isModalInitialized, setIsModalInitialized] = useState(false);

    const dragStart = useRef({ x: 0, y: 0, width: 0, height: 0 });
    const modalRef = useRef(null);
    const resizeType = useRef(null);
    const resizeStart = useRef({ x: 0, y: 0, width: 0, height: 0, left: 0, top: 0 });

    const widthModes = {
        narrow: { width: 400, label: 'Narrow', icon: faMinus },
        medium: { width: 650, label: 'Medium', icon: faArrowsAlt },
        wide: { width: 850, label: 'Wide', icon: faPlus },
        full: { width: '95vw', label: 'Full Width', icon: faExpand },
        fullscreen: { width: '98vw', label: 'Full Screen', icon: faCompress }
    };

    // ============================================================
    // THEME & DARK MODE
    // ============================================================
    const [darkMode, setDarkMode] = useState(() => {
        return document.body.classList.contains('dark-mode') || localStorage.getItem('ourzest_darkMode') === 'true';
    });
    const [currentTheme, setCurrentTheme] = useState(() => {
        return localStorage.getItem('ourzest_theme') || '#6366f1';
    });

    // ============================================================
    // TOAST
    // ============================================================
    const [toast, setToast] = useState({ show: false, type: '', message: '' });
    const toastTimerRef = useRef(null);
    const isMountedRef = useRef(true);

    const showToast = useCallback((type, message) => {
        if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
        setToast({ show: true, type, message });
        toastTimerRef.current = setTimeout(() => {
            setToast({ show: false, type: '', message: '' });
        }, 3000);
    }, []);

    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
            if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
        };
    }, []);

    // ============================================================
    // DARK MODE SYNC
    // ============================================================
    useEffect(() => {
        const syncDarkMode = () => {
            const isBodyDark = document.body.classList.contains('dark-mode') || document.body.classList.contains('ozlistui-dark');
            const isStorageDark = localStorage.getItem('ourzest_darkMode') === 'true';
            const shouldBeDark = isBodyDark || isStorageDark;
            setDarkMode(shouldBeDark);
            const container = document.querySelector('.ozlistui-container');
            if (container) {
                if (shouldBeDark) container.classList.add('ozlistui-dark');
                else container.classList.remove('ozlistui-dark');
            }
        };

        syncDarkMode();

        const observer = new MutationObserver(() => syncDarkMode());
        observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });

        const handleStorageChange = (e) => {
            if (e.key === 'ourzest_darkMode') syncDarkMode();
        };

        window.addEventListener('storage', handleStorageChange);
        return () => {
            observer.disconnect();
            window.removeEventListener('storage', handleStorageChange);
        };
    }, []);

    // ============================================================
    // THEME SYNC
    // ============================================================
    useEffect(() => {
        const applyTheme = (color) => {
            if (!color || !/^#[0-9A-F]{6}$/i.test(color)) color = '#6366f1';
            document.documentElement.style.setProperty('--ozlistui-primary', color);
            document.documentElement.style.setProperty('--ozlistui-primary-dark', color);
            document.documentElement.style.setProperty('--ozlistui-primary-light', color);
            setCurrentTheme(color);
        };

        applyTheme(currentTheme);

        const handleStorageChange = (e) => {
            if (e.key === 'ourzest_theme') applyTheme(e.newValue || '#6366f1');
        };

        const handleThemeChanged = (event) => {
            if (event.detail && event.detail.theme) applyTheme(event.detail.theme);
        };

        window.addEventListener('storage', handleStorageChange);
        window.addEventListener('themeChanged', handleThemeChanged);
        return () => {
            window.removeEventListener('storage', handleStorageChange);
            window.removeEventListener('themeChanged', handleThemeChanged);
        };
    }, [currentTheme]);

    // ============================================================
    // LANGUAGE SYNC
    // ============================================================
    useEffect(() => {
        const contextLanguage = normalizeLanguage(
            auth?.currentLanguage ||
            auth?.language ||
            user?.language ||
            user?.preferred_language ||
            localStorage.getItem('ourzest_language') ||
            'en'
        );

        if (contextLanguage !== currentLanguage) {
            setCurrentLanguage(contextLanguage);
        }
    }, [auth?.currentLanguage, auth?.language, user?.language, user?.preferred_language]);

    useEffect(() => {
        const handleLanguageChange = (event) => {
            const newLang = normalizeLanguage(event?.detail?.language || event?.detail?.lang);
            if (newLang && newLang !== currentLanguage) {
                setCurrentLanguage(newLang);
            }
        };

        window.addEventListener('languageChanged', handleLanguageChange);
        window.addEventListener('ourzest:language-change', handleLanguageChange);
        window.addEventListener('storage', (e) => {
            if (e.key === 'ourzest_language') {
                const newLang = normalizeLanguage(e.newValue);
                if (newLang !== currentLanguage) setCurrentLanguage(newLang);
            }
        });

        return () => {
            window.removeEventListener('languageChanged', handleLanguageChange);
            window.removeEventListener('ourzest:language-change', handleLanguageChange);
        };
    }, [currentLanguage]);

    // ============================================================
    // RTL DIRECTION
    // ============================================================
    useEffect(() => {
        document.documentElement.lang = currentLanguage;
        document.documentElement.dir = isRTL ? 'rtl' : 'ltr';
        document.body.dir = isRTL ? 'rtl' : 'ltr';
        const container = document.querySelector('.ozlistui-container');
        if (container) {
            if (isRTL) {
                container.classList.add('ozlistui-rtl');
                container.classList.remove('ozlistui-ltr');
            } else {
                container.classList.add('ozlistui-ltr');
                container.classList.remove('ozlistui-rtl');
            }
        }
    }, [currentLanguage, isRTL]);

    // ============================================================
    // EDIT FORM STATE
    // ============================================================
    const [editFormData, setEditFormData] = useState({
        supplier_name: '',
        company: '',
        phone: '',
        email: '',
        date: '',
        address: '',
        category: '',
        website: '',
        contact_person: '',
        notes: ''
    });
    const [editFormErrors, setEditFormErrors] = useState({});

    // ============================================================
    // PAGINATION
    // ============================================================
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(20);

    // ============================================================
    // FILTER STATES
    // ============================================================
    const [filterBy, setFilterBy] = useState('');
    const [supplierNameFilter, setSupplierNameFilter] = useState('');
    const [companyFilter, setCompanyFilter] = useState('');
    const [phoneFilter, setPhoneFilter] = useState('');
    const [emailFilter, setEmailFilter] = useState('');
    const [categoryFilter, setCategoryFilter] = useState('');
    const [addedByFilter, setAddedByFilter] = useState('');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');

    // ============================================================
    // OPTIONS (Hardcoded bilingual)
    // ============================================================
    const categoryOptions = useMemo(() => [
        { value: '', label: t.allCategories || 'All Categories' },
        { value: 'regular', label: t.regular || 'Regular' },
        { value: 'vip', label: t.vip || 'VIP' },
        { value: 'other', label: t.other || 'Other' }
    ], [t]);

    const pdfOrientationOptions = useMemo(() => [
        { value: 'portrait', label: t.portrait || 'Portrait' },
        { value: 'landscape', label: t.landscape || 'Landscape' }
    ], [t]);

    const pdfPageSizeOptions = useMemo(() => [
        { value: 'a4', label: t.a4 || 'A4' },
        { value: 'letter', label: t.letter || 'Letter' },
        { value: 'legal', label: t.legal || 'Legal' },
        { value: 'a5', label: t.a5 || 'A5' }
    ], [t]);

    const filterByOptions = useMemo(() => [
        { value: '', label: t.all || 'All' },
        { value: 'supplierName', label: t.supplierNameFilter || 'Supplier Name' },
        { value: 'company', label: t.companyFilter || 'Company Name' },
        { value: 'phone', label: t.phoneFilter || 'Phone Number' },
        { value: 'email', label: t.emailFilter || 'Email' },
        { value: 'category', label: t.categoryFilter || 'Category' },
        { value: 'date', label: t.dateRange || 'Date Range' },
        { value: 'added_by', label: t.addedByFilter || 'Added By' }
    ], [t]);

    // ============================================================
    // READ API HELPER
    // ============================================================
    const readApiJson = async (response) => {
        const raw = await response.text();
        let data;
        try {
            data = raw ? JSON.parse(raw.replace(/^\uFEFF/, '')) : null;
        } catch (_) {
            throw new Error(`Server returned invalid JSON (${response.status})`);
        }
        if (!response.ok) throw new Error(data?.message || `Server error: ${response.status}`);
        if (!data) throw new Error('Server returned an empty response');
        return data;
    };

    // ============================================================
    // FETCH SUPPLIERS
    // ============================================================
    const fetchSuppliers = useCallback(async () => {
        if (!user?.username) {
            if (isMountedRef.current) {
                showToast('error', 'User information not available');
                setLoading(false);
            }
            return;
        }
        if (!currentShop?.shop_id) {
            if (isMountedRef.current) {
                showToast('error', t.selectShop || 'Please select a shop first');
                setLoading(false);
            }
            return;
        }

        if (isMountedRef.current) setLoading(true);
        try {
            const response = await fetch(SUPPLIERS_API, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    action: 'get_records',
                    username: user.username,
                    shop_id: currentShop.shop_id,
                    language: currentLanguage
                })
            });
            const data = await readApiJson(response);
            if (data.status !== 'success') throw new Error(data.message || 'Could not load suppliers');

            if (isMountedRef.current) {
                const records = Array.isArray(data.data) ? data.data : [];
                setSuppliers(records);
                setFilteredSuppliers(records);
                setCurrentPage(1);
            }
        } catch (error) {
            console.error('Error fetching suppliers:', error);
            if (isMountedRef.current) showToast('error', error.message || t.networkError || 'Network error occurred');
        } finally {
            if (isMountedRef.current) setLoading(false);
        }
    }, [user?.username, currentShop?.shop_id, currentLanguage, showToast, t]);

    // ============================================================
    // FETCH SHOPS
    // ============================================================
    const fetchAvailableShops = useCallback(async () => {
        if (!user) return;
        setLoadingShops(true);
        try {
            const shopsData = await getShopsForSwitch();
            if (isMountedRef.current) setAvailableShops(shopsData.shops || []);
        } catch (error) {
            console.error('Error loading shops:', error);
            if (isMountedRef.current) setAvailableShops([]);
        } finally {
            if (isMountedRef.current) setLoadingShops(false);
        }
    }, [user, getShopsForSwitch]);

    // ============================================================
    // INITIAL LOAD
    // ============================================================
    useEffect(() => {
        if (user && user.username && currentShop) {
            fetchSuppliers();
            fetchAvailableShops();
        } else if (user && user.username && !currentShop) {
            setLoading(false);
        } else if (!user) {
            setLoading(false);
        }
    }, [user, currentShop?.shop_id, fetchSuppliers, fetchAvailableShops]);

    // ============================================================
    // APPLY FILTERS
    // ============================================================
    const applyFilters = useCallback(() => {
        let filtered = [...suppliers];
        if (searchTerm.trim()) {
            const term = searchTerm.toLowerCase();
            filtered = filtered.filter(supplier =>
                supplier.supplier_name?.toLowerCase().includes(term) ||
                supplier.company?.toLowerCase().includes(term) ||
                supplier.phone?.toLowerCase().includes(term) ||
                supplier.email?.toLowerCase().includes(term) ||
                supplier.address?.toLowerCase().includes(term) ||
                supplier.category?.toLowerCase().includes(term) ||
                supplier.username?.toLowerCase().includes(term) ||
                supplier.contact_person?.toLowerCase().includes(term) ||
                supplier.notes?.toLowerCase().includes(term)
            );
        }
        if (filterBy === 'supplierName' && supplierNameFilter.trim()) {
            const term = supplierNameFilter.toLowerCase();
            filtered = filtered.filter(supplier => supplier.supplier_name?.toLowerCase().includes(term));
        }
        if (filterBy === 'company' && companyFilter.trim()) {
            const term = companyFilter.toLowerCase();
            filtered = filtered.filter(supplier => supplier.company?.toLowerCase().includes(term));
        }
        if (filterBy === 'phone' && phoneFilter.trim()) {
            const term = phoneFilter.toLowerCase();
            filtered = filtered.filter(supplier => supplier.phone?.toLowerCase().includes(term));
        }
        if (filterBy === 'email' && emailFilter.trim()) {
            const term = emailFilter.toLowerCase();
            filtered = filtered.filter(supplier => supplier.email?.toLowerCase().includes(term));
        }
        if (filterBy === 'category' && categoryFilter) {
            filtered = filtered.filter(supplier => supplier.category === categoryFilter);
        }
        if (filterBy === 'added_by' && addedByFilter.trim()) {
            const term = addedByFilter.toLowerCase();
            filtered = filtered.filter(supplier => supplier.username?.toLowerCase().includes(term));
        }
        if (startDate) {
            const start = new Date(startDate);
            start.setHours(0, 0, 0, 0);
            filtered = filtered.filter(supplier => {
                if (!supplier.date) return false;
                const supplierDate = new Date(supplier.date);
                supplierDate.setHours(0, 0, 0, 0);
                return supplierDate >= start;
            });
        }
        if (endDate) {
            const end = new Date(endDate);
            end.setHours(23, 59, 59, 999);
            filtered = filtered.filter(supplier => {
                if (!supplier.date) return false;
                const supplierDate = new Date(supplier.date);
                return supplierDate <= end;
            });
        }
        setFilteredSuppliers(filtered);
        setCurrentPage(1);
    }, [suppliers, searchTerm, filterBy, supplierNameFilter, companyFilter, phoneFilter, emailFilter, categoryFilter, addedByFilter, startDate, endDate]);

    useEffect(() => { applyFilters(); }, [applyFilters]);

    // ============================================================
    // HELPER FUNCTIONS
    // ============================================================
    const formatDate = useCallback((dateString) => {
        if (!dateString) return '-';
        try {
            const date = new Date(dateString);
            if (isNaN(date.getTime())) return dateString;
            return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
        } catch { return dateString; }
    }, []);

    const getCategoryLabel = (category) => {
        const map = {
            'regular': t.regular || 'Regular',
            'vip': t.vip || 'VIP',
            'other': t.other || 'Other',
        };
        return map[category] || category || '-';
    };

    const getCategoryBadge = useCallback((category) => {
        const colors = {
            regular: { bg: '#10b98120', color: '#10b981' },
            vip: { bg: '#8b5cf620', color: '#8b5cf6' },
            other: { bg: '#6b728020', color: '#6b7280' },
        };
        const style = colors[category] || { bg: '#6b728020', color: '#6b7280' };
        return { ...style, label: getCategoryLabel(category) };
    }, [getCategoryLabel]);

    const getTodayDate = useCallback(() => new Date().toISOString().split('T')[0], []);

    // ============================================================
    // PAGINATION
    // ============================================================
    const pagination = useMemo(() => {
        const totalPages = Math.ceil(filteredSuppliers.length / itemsPerPage);
        const startIndex = (currentPage - 1) * itemsPerPage;
        const endIndex = startIndex + itemsPerPage;
        const currentSuppliers = filteredSuppliers.slice(startIndex, endIndex);
        return { totalPages, startIndex, endIndex, currentSuppliers };
    }, [filteredSuppliers, currentPage, itemsPerPage]);

    // ============================================================
    // MODAL DRAG / RESIZE
    // ============================================================
    const centerModal = useCallback(() => {
        if (!modalRef.current) return;
        const rect = modalRef.current.getBoundingClientRect();
        const left = (window.innerWidth - rect.width) / 2;
        const top = (window.innerHeight - rect.height) / 2;
        modalRef.current.style.left = `${Math.max(0, left)}px`;
        modalRef.current.style.top = `${Math.max(0, top)}px`;
        modalRef.current.style.transform = 'none';
        setModalPosition({ x: Math.max(0, left), y: Math.max(0, top) });
    }, []);

    const setWidthMode = useCallback((mode) => {
        if (!modalRef.current) return;
        setCurrentWidthMode(mode);
        const widthValue = widthModes[mode].width;
        if (mode === 'fullscreen') {
            modalRef.current.style.width = '98vw';
            modalRef.current.style.height = '90vh';
        } else {
            modalRef.current.style.width = typeof widthValue === 'number' ? `${widthValue}px` : widthValue;
            modalRef.current.style.height = 'auto';
        }
        requestAnimationFrame(() => {
            if (!modalRef.current) return;
            const rect = modalRef.current.getBoundingClientRect();
            setModalSize({ width: rect.width, height: rect.height });
            centerModal();
        });
    }, [widthModes, centerModal]);

    const startDrag = useCallback((e) => {
        if (!modalRef.current) return;
        if (e.target.closest('button, input, select, textarea, a, .ozlistui-width-btn, .ozlistui-close-btn')) return;
        e.preventDefault();
        setIsDragging(true);
        const rect = modalRef.current.getBoundingClientRect();
        dragStart.current = {
            x: e.clientX - rect.left,
            y: e.clientY - rect.top,
            width: rect.width,
            height: rect.height
        };
        modalRef.current.style.transition = 'none';
    }, []);

    const startResize = useCallback((e, type) => {
        e.preventDefault();
        e.stopPropagation();
        if (!modalRef.current) return;
        setIsResizing(true);
        resizeType.current = type;
        const rect = modalRef.current.getBoundingClientRect();
        resizeStart.current = {
            x: e.clientX, y: e.clientY,
            width: rect.width, height: rect.height,
            left: rect.left, top: rect.top,
            minWidth: 350, minHeight: 400,
            maxWidth: window.innerWidth - 40, maxHeight: window.innerHeight - 40
        };
        modalRef.current.style.transition = 'none';
    }, []);

    useEffect(() => {
        const handleMouseMove = (e) => {
            if (isDragging && modalRef.current) {
                let newLeft = e.clientX - dragStart.current.x;
                let newTop = e.clientY - dragStart.current.y;
                newLeft = Math.max(0, Math.min(newLeft, window.innerWidth - dragStart.current.width));
                newTop = Math.max(0, Math.min(newTop, window.innerHeight - dragStart.current.height));
                modalRef.current.style.left = `${newLeft}px`;
                modalRef.current.style.top = `${newTop}px`;
                modalRef.current.style.transform = 'none';
                setModalPosition({ x: newLeft, y: newTop });
            }

            if (isResizing && modalRef.current && resizeType.current) {
                let newWidth = resizeStart.current.width;
                let newHeight = resizeStart.current.height;
                let newLeft = resizeStart.current.left;
                let newTop = resizeStart.current.top;
                const deltaX = e.clientX - resizeStart.current.x;
                const deltaY = e.clientY - resizeStart.current.y;

                switch (resizeType.current) {
                    case 'e': newWidth = Math.min(resizeStart.current.maxWidth, Math.max(resizeStart.current.minWidth, resizeStart.current.width + deltaX)); break;
                    case 'w': newWidth = Math.min(resizeStart.current.maxWidth, Math.max(resizeStart.current.minWidth, resizeStart.current.width - deltaX)); newLeft = resizeStart.current.left + (resizeStart.current.width - newWidth); break;
                    case 's': newHeight = Math.min(resizeStart.current.maxHeight, Math.max(resizeStart.current.minHeight, resizeStart.current.height + deltaY)); break;
                    case 'n': newHeight = Math.min(resizeStart.current.maxHeight, Math.max(resizeStart.current.minHeight, resizeStart.current.height - deltaY)); newTop = resizeStart.current.top + (resizeStart.current.height - newHeight); break;
                    case 'ne': newWidth = Math.min(resizeStart.current.maxWidth, Math.max(resizeStart.current.minWidth, resizeStart.current.width + deltaX)); newHeight = Math.min(resizeStart.current.maxHeight, Math.max(resizeStart.current.minHeight, resizeStart.current.height - deltaY)); newTop = resizeStart.current.top + (resizeStart.current.height - newHeight); break;
                    case 'nw': newWidth = Math.min(resizeStart.current.maxWidth, Math.max(resizeStart.current.minWidth, resizeStart.current.width - deltaX)); newHeight = Math.min(resizeStart.current.maxHeight, Math.max(resizeStart.current.minHeight, resizeStart.current.height - deltaY)); newLeft = resizeStart.current.left + (resizeStart.current.width - newWidth); newTop = resizeStart.current.top + (resizeStart.current.height - newHeight); break;
                    case 'se': newWidth = Math.min(resizeStart.current.maxWidth, Math.max(resizeStart.current.minWidth, resizeStart.current.width + deltaX)); newHeight = Math.min(resizeStart.current.maxHeight, Math.max(resizeStart.current.minHeight, resizeStart.current.height + deltaY)); break;
                    case 'sw': newWidth = Math.min(resizeStart.current.maxWidth, Math.max(resizeStart.current.minWidth, resizeStart.current.width - deltaX)); newHeight = Math.min(resizeStart.current.maxHeight, Math.max(resizeStart.current.minHeight, resizeStart.current.height + deltaY)); newLeft = resizeStart.current.left + (resizeStart.current.width - newWidth); break;
                }
                modalRef.current.style.width = `${newWidth}px`;
                modalRef.current.style.height = `${newHeight}px`;
                modalRef.current.style.left = `${newLeft}px`;
                modalRef.current.style.top = `${newTop}px`;
                setModalSize({ width: newWidth, height: newHeight });
                setModalPosition({ x: newLeft, y: newTop });
            }
        };

        const handleMouseUp = () => {
            if (isDragging || isResizing) {
                setIsDragging(false);
                setIsResizing(false);
                resizeType.current = null;
                if (modalRef.current) modalRef.current.style.transition = '';
            }
        };

        if (isDragging || isResizing) {
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
        }

        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };
    }, [isDragging, isResizing]);

    useEffect(() => {
        if (showEditModal && modalRef.current && !isModalInitialized) {
            setTimeout(() => { centerModal(); setWidthMode('wide'); setIsModalInitialized(true); }, 50);
        }
        if (!showEditModal) setIsModalInitialized(false);
    }, [showEditModal, centerModal, setWidthMode, isModalInitialized]);

    useEffect(() => {
        const handleWindowResize = () => {
            if (!modalRef.current || !showEditModal) return;
            const rect = modalRef.current.getBoundingClientRect();
            let newLeft = Math.min(rect.left, window.innerWidth - rect.width);
            let newTop = Math.min(rect.top, window.innerHeight - rect.height);
            newLeft = Math.max(0, newLeft);
            newTop = Math.max(0, newTop);
            if (newLeft !== rect.left || newTop !== rect.top) {
                modalRef.current.style.left = `${newLeft}px`;
                modalRef.current.style.top = `${newTop}px`;
                setModalPosition({ x: newLeft, y: newTop });
            }
        };
        window.addEventListener('resize', handleWindowResize);
        return () => window.removeEventListener('resize', handleWindowResize);
    }, [showEditModal]);

    // ============================================================
    // HANDLE ACTIONS
    // ============================================================
    const handleSwitchShop = useCallback(async (shop) => {
        try {
            const success = await switchShop(shop.shop_id, shop.shop_name, shop.shop_code);
            if (success) {
                showToast('success', t.shopSwitched || 'Shop switched successfully');
                setCurrentPage(1);
                setShowShopSelector(false);
                fetchSuppliers();
            } else {
                showToast('error', t.shopSwitchFailed || 'Failed to switch shop');
            }
        } catch (error) {
            console.error('Error switching shop:', error);
            showToast('error', t.shopSwitchFailed || 'Failed to switch shop');
        }
    }, [switchShop, showToast, t, fetchSuppliers]);

    const handleView = useCallback((supplier) => {
        setSelectedSupplier(supplier);
        setShowViewModal(true);
    }, []);

    const handleEdit = useCallback((supplier) => {
        const sourceSupplier = supplier.__source || supplier;
        setSelectedSupplier(supplier);
        setEditFormData({
            supplier_name: sourceSupplier.supplier_name || '',
            company: sourceSupplier.company || '',
            phone: sourceSupplier.phone || '',
            email: sourceSupplier.email || '',
            date: sourceSupplier.date || '',
            address: sourceSupplier.address || '',
            category: sourceSupplier.category || '',
            website: sourceSupplier.website || '',
            contact_person: sourceSupplier.contact_person || '',
            notes: sourceSupplier.notes || ''
        });
        setEditFormErrors({});
        setShowEditModal(true);
    }, []);

    const handleDeleteClick = useCallback((supplierId) => {
        const supplier = suppliers.find(s => s.supplierid === supplierId);
        if (!supplier) return;
        if (supplier.username !== user?.username) {
            showToast('error', 'You can only delete suppliers you added');
            return;
        }
        setSupplierToDelete(supplierId);
        setShowDeleteModal(true);
    }, [suppliers, user, showToast]);

    const handleDelete = useCallback(async () => {
        if (!supplierToDelete || !currentShop) return;
        const supplier = suppliers.find(s => s.supplierid === supplierToDelete);
        if (!supplier || supplier.username !== user?.username) {
            showToast('error', 'You can only delete your own suppliers');
            setShowDeleteModal(false);
            setSupplierToDelete(null);
            return;
        }
        try {
            const response = await fetch(SUPPLIERS_API, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    action: 'delete',
                    supplierid: supplierToDelete,
                    username: user.username,
                    shop_id: currentShop.shop_id,
                    language: currentLanguage
                })
            });
            const data = await readApiJson(response);
            if (data.status === 'success') {
                showToast('success', t.deletedSuccess || 'Supplier deleted successfully');
                fetchSuppliers();
                setShowDeleteModal(false);
                setSupplierToDelete(null);
            } else {
                showToast('error', data.message || t.deleteError || 'Error deleting supplier');
            }
        } catch (error) {
            console.error('Error deleting supplier:', error);
            showToast('error', t.deleteError || 'Error deleting supplier');
        }
    }, [supplierToDelete, currentShop, suppliers, user, currentLanguage, t, showToast, fetchSuppliers]);

    const handleUpdateSupplier = useCallback(async (e) => {
        e.preventDefault();
        if (!selectedSupplier || !currentShop) return;
        const errors = {};
        if (!editFormData.supplier_name.trim()) errors.supplier_name = t.supplierNameRequired || 'Supplier name is required';
        if (editFormData.phone && !/^[\d\s\-\+\(\)]{10,15}$/.test(editFormData.phone)) errors.phone = t.invalidPhone || 'Please enter a valid phone number';
        if (editFormData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(editFormData.email)) errors.email = t.invalidEmail || 'Please enter a valid email address';
        if (Object.keys(errors).length > 0) { setEditFormErrors(errors); return; }
        setEditLoading(true);
        try {
            const response = await fetch(SUPPLIERS_API, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    action: 'edit',
                    supplierid: selectedSupplier.supplierid,
                    username: user.username,
                    shop_id: currentShop.shop_id,
                    language: currentLanguage,
                    ...editFormData
                })
            });
            const data = await readApiJson(response);
            if (data.status === 'success') {
                showToast('success', t.supplierUpdated || 'Supplier updated successfully');
                setShowEditModal(false);
                fetchSuppliers();
            } else {
                showToast('error', data.message || t.updateError || 'Error updating supplier');
            }
        } catch (error) {
            console.error('Error updating supplier:', error);
            showToast('error', t.updateError || 'Error updating supplier');
        } finally {
            setEditLoading(false);
        }
    }, [selectedSupplier, currentShop, editFormData, user, currentLanguage, t, showToast, fetchSuppliers]);

    const handlePageChange = useCallback((page) => {
        if (page >= 1 && page <= pagination.totalPages) setCurrentPage(page);
    }, [pagination.totalPages]);

    const resetFilters = useCallback(() => {
        setFilterBy('');
        setSupplierNameFilter('');
        setCompanyFilter('');
        setPhoneFilter('');
        setEmailFilter('');
        setCategoryFilter('');
        setAddedByFilter('');
        setStartDate('');
        setEndDate('');
        setSearchTerm('');
    }, []);

    // ============================================================
    // PDF EXPORT — VECTOR-BASED (jsPDF + autoTable), NOT html2canvas
    // ============================================================
    const exportToPDF = useCallback(async () => {
        if (!filteredSuppliers?.length) {
            showToast('warning', t.noDataToExport || 'No data to export');
            return;
        }
        setPdfLoading(true);
        try {
            const urdu = currentLanguage === 'ur';
            const doc = new jsPDF({
                orientation: pdfOrientation === 'landscape' ? 'landscape' : 'portrait',
                unit: 'mm', compress: true,
                format: ['a4', 'a5', 'letter', 'legal'].includes(pdfPageSize) ? pdfPageSize : 'a4'
            });
            doc.setR2L(false);
            const labels = urdu ? {
                title: 'سپلائر کی فہرست', total: 'کل سپلائر', generated: 'تیاری کی تاریخ',
                report: 'رپورٹ نمبر', code: 'دکان کا کوڈ', page: 'صفحہ'
            } : {
                title: 'Supplier Directory', total: 'Total suppliers', generated: 'Generated',
                report: 'Report ID', code: 'Shop code', page: 'Page'
            };
            const columns = [
                { key: 'serial', title: urdu ? 'شمار' : 'No.', weight: 6 },
                { key: 'name', title: urdu ? 'سپلائر کا نام' : 'Supplier name', weight: 19 },
                { key: 'company', title: urdu ? 'کمپنی' : 'Company', weight: 16 },
                { key: 'phone', title: urdu ? 'فون نمبر' : 'Phone', weight: 14 },
                { key: 'email', title: urdu ? 'ای میل' : 'Email', weight: 18 },
                { key: 'date', title: urdu ? 'تاریخ' : 'Date', weight: 11 },
                { key: 'category', title: urdu ? 'کیٹیگری' : 'Category', weight: 10 },
                { key: 'user', title: urdu ? 'ایڈ کردہ' : 'Added by', weight: 12 }
            ];
            const rows = filteredSuppliers.map((supplier, index) => ({
                serial: String(index + 1), name: String(supplier.supplier_name || '-'),
                company: String(supplier.company || '-'), phone: String(supplier.phone || '-'),
                email: String(supplier.email || '-'), date: String(formatDate(supplier.date) || '-'),
                category: String(getCategoryLabel(supplier.category) || '-'), user: String(supplier.username || '-')
            }));
            const shopName = String(currentShop?.shop_name || labels.title);
            const shopCode = String(currentShop?.shop_code || '-');
            const generated = new Date();
            const stamp = generated.toLocaleString('en-GB', { hour12: false });
            const reportId = `${getTodayDate().replace(/-/g, '')}-${String(generated.getTime()).slice(-6)}`;
            const text = await createNooriPdfText(doc,
                urdu || [shopName, shopCode, ...rows.flatMap(Object.values)].some(hasUrdu));
            const width = doc.internal.pageSize.getWidth();
            const height = doc.internal.pageSize.getHeight();
            const margin = 10;
            const usable = width - margin * 2;
            const color = /^#[0-9a-f]{6}$/i.test(currentTheme || '') ? currentTheme : '#2563eb';
            const accent = [1, 3, 5].map(index => parseInt(color.slice(index, index + 2), 16));
            const ink = [30, 41, 59];
            const muted = [85, 100, 120];
            const padding = 2.2;
            const weight = columns.reduce((sum, column) => sum + column.weight, 0);
            columns.forEach(column => { column.width = usable * column.weight / weight; });
            const ordered = urdu ? [...columns].reverse() : columns;
            const title = text.layout(shopName, { size: hasUrdu(shopName) ? 20 : 16, maxWidth: usable, bold: true });
            const subtitle = text.layout(labels.title, { size: urdu ? 14 : 10, maxWidth: usable });
            const half = (usable - 6) / 2;
            const metadata = (label, value) => {
                const caption = text.layout(`${label}:`, { size: urdu ? 10.5 : 8, maxWidth: half * 0.48 });
                const body = text.layout(value, { size: hasUrdu(value) ? 10.5 : 8,
                    maxWidth: half - caption.width - 2 });
                return { caption, body, height: Math.max(caption.height, body.height) };
            };
            const fields = [metadata(labels.total, String(rows.length)), metadata(labels.generated, stamp),
                metadata(labels.report, reportId), metadata(labels.code, shopCode)];
            const titleY = 10;
            const subtitleY = titleY + title.height + 2;
            const metaY = subtitleY + subtitle.height + 4;
            const metaRowHeight = [Math.max(fields[0].height, fields[1].height),
                Math.max(fields[2].height, fields[3].height)];
            const ruleY = metaY + metaRowHeight[0] + metaRowHeight[1] + 5;
            const tableTop = ruleY + 4;
            const drawField = (field, x, top) => {
                if (urdu) {
                    text.draw(field.caption, x + half - field.caption.width, top,
                        field.caption.width, 'right', muted);
                    text.draw(field.body, x, top, half - field.caption.width - 2, 'right', muted);
                } else {
                    text.draw(field.caption, x, top, field.caption.width, 'left', muted);
                    text.draw(field.body, x + field.caption.width + 2, top,
                        half - field.caption.width - 2, 'left', muted);
                }
            };
            const header = () => {
                text.draw(title, margin, titleY, usable, 'center', ink);
                text.draw(subtitle, margin, subtitleY, usable, 'center', muted);
                fields.forEach((field, index) => {
                    const logicalColumn = index % 2;
                    const visualColumn = urdu ? 1 - logicalColumn : logicalColumn;
                    drawField(field, margin + visualColumn * (half + 6),
                        metaY + (index > 1 ? metaRowHeight[0] + 2 : 0));
                });
                doc.setDrawColor(...accent);
                doc.setLineWidth(0.55);
                doc.line(margin, ruleY, width - margin, ruleY);
            };
            const headLayouts = Object.fromEntries(columns.map(column => [column.key,
                text.layout(column.title, { size: urdu ? (column.key === 'serial' ? 10.5 : 13) : (usable < 150 ? 7.5 : 8.5),
                    maxWidth: column.width - padding * 2, bold: !urdu })]));
            const headHeight = Math.max(...Object.values(headLayouts).map(block => block.height)) + padding * 2;
            const maxBodyHeight = height - 17 - tableTop - headHeight - padding * 2 - 1;
            if (maxBodyHeight < 12) throw new Error('The report header is too tall for this page size. Use a larger page or a shorter shop name.');

            // Rows are measured from HarfBuzz's positioned outlines. Oversized
            // cells are split into line fragments before AutoTable paginates them.
            const body = [];
            for (const row of rows) {
                const chunks = Object.fromEntries(columns.map(column => {
                    const value = row[column.key];
                    const layout = text.layout(value, {
                        size: hasUrdu(value) ? (usable < 150 ? 11.5 : 13) : (usable < 150 ? 7.5 : 8.5),
                        maxWidth: column.width - padding * 2
                    });
                    return [column.key, text.split(layout, maxBodyHeight)];
                }));
                const fragmentCount = Math.max(...Object.values(chunks).map(parts => parts.length));
                for (let fragment = 0; fragment < fragmentCount; fragment++) {
                    body.push(Object.fromEntries(columns.map(column => [column.key, {
                        content: '', nooriBlock: chunks[column.key][fragment] || text.layout(''),
                        nooriAlign: column.key === 'serial' ? 'center'
                            : text.baseDirection(row[column.key]) === 'rtl' ? 'right' : 'left'
                    }])));
                }
            }
            const cellBlocks = new WeakMap();
            drawPdfTable(doc, {
                columns: ordered.map(column => ({ header: column.title, dataKey: column.key })), body,
                startY: tableTop, margin: { top: tableTop, bottom: 17, left: margin, right: margin },
                tableWidth: usable, theme: 'grid', pageBreak: 'auto', rowPageBreak: 'avoid', showHead: 'everyPage',
                styles: { font: 'helvetica', fontSize: 1, cellPadding: padding, valign: 'middle',
                    lineWidth: 0.12, lineColor: [221, 227, 235], textColor: ink },
                headStyles: { fillColor: accent, textColor: [255, 255, 255] },
                alternateRowStyles: { fillColor: [246, 248, 251] },
                columnStyles: Object.fromEntries(columns.map(column => [column.key, { cellWidth: column.width }])),
                didParseCell: data => {
                    const block = data.section === 'head' ? headLayouts[data.column.dataKey] : data.cell.raw.nooriBlock;
                    const align = data.section === 'head' ? 'center' : data.cell.raw.nooriAlign;
                    cellBlocks.set(data.cell, { block, align });
                    data.cell.text = [''];
                    data.cell.styles.minCellHeight = block.height + padding * 2;
                },
                didDrawCell: data => {
                    const saved = cellBlocks.get(data.cell);
                    if (!saved) throw new Error('PDF cell layout was not preserved during pagination.');
                    text.draw(saved.block, data.cell.x + padding,
                        data.cell.y + (data.cell.height - saved.block.height) / 2,
                        data.cell.width - padding * 2, saved.align,
                        data.section === 'head' ? [255, 255, 255] : ink);
                },
                didDrawPage: header
            });
            const originalPages = doc.internal.getNumberOfPages();
            let first = 1;
            let last = originalPages;
            if (pdfPageRange === 'range') {
                first = Math.trunc(Number(pdfStartPage));
                last = Math.trunc(Number(pdfEndPage));
                if (!Number.isFinite(first) || !Number.isFinite(last) || first < 1 || last < first || first > originalPages) {
                    throw new Error(`Invalid page range. This report has ${originalPages} pages.`);
                }
                last = Math.min(last, originalPages);
                for (let page = originalPages; page > 0; page--) {
                    if (page < first || page > last) doc.deletePage(page);
                }
            }
            const pageCount = doc.internal.getNumberOfPages();
            const pageLabel = text.layout(labels.page, { size: urdu ? 10 : 8 });
            for (let page = 1; page <= pageCount; page++) {
                doc.setPage(page);
                doc.setDrawColor(221, 227, 235);
                doc.setLineWidth(0.12);
                doc.line(margin, height - 13, width - margin, height - 13);
                const number = text.layout(`${page} / ${pageCount}`, { size: 8, direction: 'ltr' });
                text.draw(number, width / 2 - 8, height - 9, 16, 'center', muted);
                text.draw(pageLabel, urdu ? width / 2 + 10 : width / 2 - 10 - pageLabel.width,
                    height - 9, pageLabel.width, 'left', muted);
                if (first !== 1 || last !== originalPages) {
                    text.draw(text.layout(`${first + page - 1} / ${originalPages}`, { size: 7 }),
                        width - margin - 24, height - 9, 24, 'right', muted);
                }
            }
            doc.setProperties({ title: labels.title, subject: shopName,
                author: user?.business_name || 'OurZest', creator: 'OurZest' });
            const safeShop = shopName.replace(/[<>:"/\\|?*\x00-\x1F]/g, '_').slice(0, 70);
            doc.save(`Supplier_Report_${safeShop}_${getTodayDate()}.pdf`);
            showToast('success', t.reportGenerated || 'Report generated!');
        } catch (error) {
            console.error('PDF Error:', error);
            showToast('error', `${t.pdfGenerationError || 'Error generating PDF'}: ${error.message || String(error)}`);
        } finally { setPdfLoading(false); }
    }, [filteredSuppliers, currentLanguage, pdfOrientation, pdfPageSize, pdfPageRange,
        pdfStartPage, pdfEndPage, t, currentShop, user, showToast, currentTheme,
        getCategoryLabel, formatDate, getTodayDate]);


    // ============================================================
    // RENDER
    // ============================================================
    return (
        <div className={`ozlistui-container ${isRTL ? 'ozlistui-rtl' : 'ozlistui-ltr'} ${darkMode ? 'ozlistui-dark' : ''}`}>
            <ToastNotification toast={toast} setToast={setToast} t={t} isRTL={isRTL} />

            <ShopSelectorModal
                isOpen={showShopSelector}
                onClose={() => setShowShopSelector(false)}
                shops={availableShops}
                currentShop={currentShop}
                onSelectShop={handleSwitchShop}
                t={t}
                loading={loadingShops}
                isRTL={isRTL}
            />

            <div className="ozlistui-sticky-header">
                <div className="ozlistui-header-bg-animation">
                    <div className="gradient-orb orb-1"></div>
                    <div className="gradient-orb orb-2"></div>
                    <div className="gradient-orb orb-3"></div>
                    <div className="gradient-orb orb-4"></div>
                    <div className="gradient-orb orb-5"></div>
                </div>
                <div className="ozlistui-header-content">
                    <div className="ozlistui-header-left">
                        <div className="ozlistui-header-title">
                            <h1>{t.supplierList}</h1>
                            <p>{t.formDetails}</p>
                        </div>
                    </div>
                    <div className="ozlistui-header-right">
                        <button
                            className="ozlistui-header-filter-btn"
                            onClick={() => setShowFilters(!showFilters)}
                            title={showFilters ? (t.hideFilters || 'Hide Filters') : (t.filters || 'Filters')}
                        >
                            <FontAwesomeIcon icon={showFilters ? faEyeSlash : faFilter} />
                            <span>{showFilters ? (t.hideFilters || 'Hide Filters') : (t.filters || 'Filters')}</span>
                        </button>

                        {!currentShop && (
                            <button className="ozlistui-header-shop-btn" onClick={() => setShowShopSelector(true)}>
                                <FontAwesomeIcon icon={faExchangeAlt} />
                                <span>{t.switchShop}</span>
                            </button>
                        )}
                    </div>
                </div>
            </div>

            {showFilters && (
                <div className="ozlistui-filters-section">
                    <div className="ozlistui-filters-row">
                        <CustomDropdown
                            label={t.filterBy || 'Filter By'}
                            icon={faTag}
                            value={filterBy}
                            onChange={setFilterBy}
                            options={filterByOptions}
                            t={t}
                            isRTL={isRTL}
                        />

                        {filterBy === 'supplierName' && (
                            <div className="ozlistui-filter-group">
                                <div className="ozlistui-floating-wrapper">
                                    <input
                                        type="text"
                                        id="supplierNameFilter"
                                        value={supplierNameFilter}
                                        onChange={(e) => setSupplierNameFilter(e.target.value)}
                                        placeholder=" "
                                        dir={isRTL ? 'rtl' : 'ltr'}
                                    />
                                    <label htmlFor="supplierNameFilter">
                                        <FontAwesomeIcon icon={faUser} />
                                        {t.supplierNameFilter}
                                    </label>
                                </div>
                            </div>
                        )}

                        {filterBy === 'company' && (
                            <div className="ozlistui-filter-group">
                                <div className="ozlistui-floating-wrapper">
                                    <input
                                        type="text"
                                        id="companyFilter"
                                        value={companyFilter}
                                        onChange={(e) => setCompanyFilter(e.target.value)}
                                        placeholder=" "
                                        dir={isRTL ? 'rtl' : 'ltr'}
                                    />
                                    <label htmlFor="companyFilter">
                                        <FontAwesomeIcon icon={faStore} />
                                        {t.companyFilter}
                                    </label>
                                </div>
                            </div>
                        )}

                        {filterBy === 'phone' && (
                            <div className="ozlistui-filter-group">
                                <div className="ozlistui-floating-wrapper">
                                    <input
                                        type="tel"
                                        id="phoneFilter"
                                        value={phoneFilter}
                                        onChange={(e) => setPhoneFilter(e.target.value)}
                                        placeholder=" "
                                        dir="ltr"
                                    />
                                    <label htmlFor="phoneFilter">
                                        <FontAwesomeIcon icon={faPhone} />
                                        {t.phoneFilter}
                                    </label>
                                </div>
                            </div>
                        )}

                        {filterBy === 'email' && (
                            <div className="ozlistui-filter-group">
                                <div className="ozlistui-floating-wrapper">
                                    <input
                                        type="email"
                                        id="emailFilter"
                                        value={emailFilter}
                                        onChange={(e) => setEmailFilter(e.target.value)}
                                        placeholder=" "
                                        dir="ltr"
                                    />
                                    <label htmlFor="emailFilter">
                                        <FontAwesomeIcon icon={faEnvelope} />
                                        {t.emailFilter}
                                    </label>
                                </div>
                            </div>
                        )}

                        {filterBy === 'category' && (
                            <CustomDropdown
                                label={t.categoryFilter || 'Category'}
                                icon={faTag}
                                value={categoryFilter}
                                onChange={setCategoryFilter}
                                options={categoryOptions}
                                t={t}
                                isRTL={isRTL}
                            />
                        )}

                        {filterBy === 'date' && (
                            <>
                                <div className="ozlistui-filter-group ozlistui-date-filter">
                                    <div className="ozlistui-floating-wrapper">
                                        <input
                                            type="date"
                                            id="startDate"
                                            value={startDate}
                                            onChange={(e) => setStartDate(e.target.value)}
                                            max={getTodayDate()}
                                            placeholder=" "
                                            dir={isRTL ? 'rtl' : 'ltr'}
                                        />
                                        <label htmlFor="startDate">
                                            <FontAwesomeIcon icon={faCalendar} />
                                            {t.startDate}
                                        </label>
                                    </div>
                                </div>
                                <div className="ozlistui-filter-group ozlistui-date-filter">
                                    <div className="ozlistui-floating-wrapper">
                                        <input
                                            type="date"
                                            id="endDate"
                                            value={endDate}
                                            onChange={(e) => setEndDate(e.target.value)}
                                            min={startDate}
                                            max={getTodayDate()}
                                            placeholder=" "
                                            dir={isRTL ? 'rtl' : 'ltr'}
                                        />
                                        <label htmlFor="endDate">
                                            <FontAwesomeIcon icon={faCalendar} />
                                            {t.endDate}
                                        </label>
                                    </div>
                                </div>
                            </>
                        )}

                        {filterBy === 'added_by' && (
                            <div className="ozlistui-filter-group">
                                <div className="ozlistui-floating-wrapper">
                                    <input
                                        type="text"
                                        id="addedByFilter"
                                        value={addedByFilter}
                                        onChange={(e) => setAddedByFilter(e.target.value)}
                                        placeholder=" "
                                        dir={isRTL ? 'rtl' : 'ltr'}
                                    />
                                    <label htmlFor="addedByFilter">
                                        <FontAwesomeIcon icon={faUser} />
                                        {t.addedByFilter}
                                    </label>
                                </div>
                            </div>
                        )}

                        <CustomDropdown
                            label={t.orientation || 'Orientation'}
                            icon={faFilePdf}
                            value={pdfOrientation}
                            onChange={setPdfOrientation}
                            options={pdfOrientationOptions}
                            t={t}
                            isRTL={isRTL}
                        />

                        <CustomDropdown
                            label={t.pageSize || 'Page Size'}
                            icon={faFilePdf}
                            value={pdfPageSize}
                            onChange={setPdfPageSize}
                            options={pdfPageSizeOptions}
                            t={t}
                            isRTL={isRTL}
                        />

                        <div className="ozlistui-filter-group">
                            <button onClick={fetchSuppliers} className="ozlistui-refresh-btn" disabled={loading}>
                                <FontAwesomeIcon icon={faRefresh} />
                                <span className="ozlistui-btn-text">{t.refresh}</span>
                            </button>
                        </div>

                        <div className="ozlistui-filter-group">
                            <button
                                onClick={exportToPDF}
                                className="ozlistui-refresh-btn"
                                disabled={filteredSuppliers.length === 0 || pdfLoading}
                                style={{ background: 'linear-gradient(135deg, #dc2626, #b91c1c)', color: 'white' }}
                            >
                                {pdfLoading ? <div className="ozlistui-btn-spinner-small"></div> : <FontAwesomeIcon icon={faFilePdf} />}
                                <span className="ozlistui-btn-text">{t.exportToPDF}</span>
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {loading ? (
                <div className="ozlistui-loading-container">
                    <div className="ozlistui-loading-spinner"></div>
                    <p>{t.loadingUserData}</p>
                </div>
            ) : !currentShop ? (
                <div className="ozlistui-no-shop-container">
                    <div className="ozlistui-no-shop-message">
                        <FontAwesomeIcon icon={faStore} />
                        <h3>{t.noShopSelected}</h3>
                        <p>{t.selectShop}</p>
                        <button className="ozlistui-btn ozlistui-btn-primary" onClick={() => setShowShopSelector(true)}>
                            <FontAwesomeIcon icon={faExchangeAlt} />
                            <span>{t.switchShop}</span>
                        </button>
                    </div>
                </div>
            ) : filteredSuppliers.length === 0 ? (
                <div className="ozlistui-no-shop-container">
                    <div className="ozlistui-no-shop-message">
                        <FontAwesomeIcon icon={faUser} />
                        <h3>{t.noSuppliers || 'No suppliers found'}</h3>
                        <p>{t.searchPlaceholder || 'Try adjusting your search or filters'}</p>
                    </div>
                </div>
            ) : (
                <>
                    <div className="ozlistui-table-scroll">
                        <div className="ozlistui-table-container">
                            <div className="ozlistui-table-wrapper">
                                <table className="ozlistui-table">
                                    <thead>
                                        <tr>
                                            <th className="ozlistui-id-column ozlistui-centered-cell">{t.serialNo}</th>
                                            <th className="ozlistui-centered-cell">{t.supplierName}</th>
                                            <th className="ozlistui-centered-cell">{t.company}</th>
                                            <th className="ozlistui-centered-cell">{t.phone}</th>
                                            <th className="ozlistui-centered-cell">{t.date}</th>
                                            <th className="ozlistui-centered-cell">{t.category}</th>
                                            <th className="ozlistui-centered-cell">{t.addedBy}</th>
                                            <th className="ozlistui-actions-column ozlistui-centered-cell">{t.actions}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {pagination.currentSuppliers.map((supplier, index) => {
                                            const addedBy = supplier.username || '-';
                                            const isSelfAdded = addedBy === user?.username;
                                            const categoryStyle = getCategoryBadge(supplier.category);
                                            return (
                                                <tr key={supplier.supplierid} onClick={() => handleView(supplier)}>
                                                    <td className="ozlistui-id-cell ozlistui-centered-cell" data-label={t.serialNo}>
                                                        <span className="ozlistui-cell-text">{pagination.startIndex + index + 1}</span>
                                                    </td>
                                                    <td className="ozlistui-centered-cell" data-label={t.supplierName}>
                                                        <span className="ozlistui-cell-text ozlistui-supplier-name" dir={isRTL ? 'rtl' : 'ltr'}>{supplier.supplier_name || '-'}</span>
                                                    </td>
                                                    <td className="ozlistui-centered-cell" data-label={t.company}>
                                                        <span className="ozlistui-cell-text ozlistui-company-name" dir={isRTL ? 'rtl' : 'ltr'}>{supplier.company || '-'}</span>
                                                    </td>
                                                    <td className="ozlistui-centered-cell" data-label={t.phone}>
                                                        <span className="ozlistui-cell-text" dir="ltr">{supplier.phone || '-'}</span>
                                                    </td>
                                                    <td className="ozlistui-centered-cell" data-label={t.date}>
                                                        <span className="ozlistui-cell-text" dir={isRTL ? 'rtl' : 'ltr'}>{formatDate(supplier.date)}</span>
                                                    </td>
                                                    <td className="ozlistui-centered-cell" data-label={t.category}>
                                                        {supplier.category ? (
                                                            <div className="ozlistui-category-badge" style={{ backgroundColor: categoryStyle.bg, color: categoryStyle.color }}>
                                                                {categoryStyle.label}
                                                            </div>
                                                        ) : '-'}
                                                    </td>
                                                    <td className="ozlistui-centered-cell" data-label={t.addedBy}>
                                                        <span className={`ozlistui-added-by-badge ${isSelfAdded ? 'ozlistui-self' : ''}`}>
                                                            <span className="ozlistui-cell-text" dir={isRTL ? 'rtl' : 'ltr'}>{addedBy}</span>
                                                        </span>
                                                    </td>
                                                    <td className="ozlistui-actions-cell ozlistui-centered-cell" data-label={t.actions}>
                                                        <div className="ozlistui-actions-content">
                                                            <button
                                                                onClick={(event) => { event.stopPropagation(); handleView(supplier); }}
                                                                className="ozlistui-action-btn ozlistui-view-btn"
                                                                title={t.view}
                                                                aria-label={`${t.view} ${supplier.supplier_name || ''}`}
                                                            >
                                                                <FontAwesomeIcon icon={faEye} />
                                                            </button>
                                                            <button
                                                                onClick={(event) => { event.stopPropagation(); handleEdit(supplier); }}
                                                                className="ozlistui-action-btn ozlistui-edit-btn"
                                                                title={t.edit}
                                                                aria-label={`${t.edit} ${supplier.supplier_name || ''}`}
                                                            >
                                                                <FontAwesomeIcon icon={faEdit} />
                                                            </button>
                                                            <button
                                                                onClick={(event) => { event.stopPropagation(); handleDeleteClick(supplier.supplierid); }}
                                                                className="ozlistui-action-btn ozlistui-delete-btn"
                                                                title={t.delete}
                                                                aria-label={`${t.delete} ${supplier.supplier_name || ''}`}
                                                                disabled={!isSelfAdded}
                                                                style={{ opacity: isSelfAdded ? 1 : 0.5, cursor: isSelfAdded ? 'pointer' : 'not-allowed' }}
                                                            >
                                                                <FontAwesomeIcon icon={faTrash} />
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>

                    {filteredSuppliers.length > 0 && currentShop && (
                        <div className="ozlistui-pagination">
                            <div className="ozlistui-pagination-info">
                                <span>{t.showing} {pagination.startIndex + 1} {t.of} {filteredSuppliers.length} {t.suppliers}</span>
                            </div>
                            <div className="ozlistui-pagination-controls">
                                <button onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} className="ozlistui-pagination-btn">
                                    <FontAwesomeIcon icon={faChevronLeft} />
                                    <span>{t.previous}</span>
                                </button>
                                <div className="ozlistui-page-numbers">
                                    {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                                        let pageNum;
                                        if (pagination.totalPages <= 5) {
                                            pageNum = i + 1;
                                        } else if (currentPage <= 3) {
                                            pageNum = i + 1;
                                        } else if (currentPage >= pagination.totalPages - 2) {
                                            pageNum = pagination.totalPages - 4 + i;
                                        } else {
                                            pageNum = currentPage - 2 + i;
                                        }
                                        return (
                                            <button key={pageNum} onClick={() => handlePageChange(pageNum)} className={`ozlistui-page-number ${currentPage === pageNum ? 'ozlistui-active' : ''}`}>
                                                {pageNum}
                                            </button>
                                        );
                                    })}
                                </div>
                                <button onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === pagination.totalPages} className="ozlistui-pagination-btn">
                                    <span>{t.next}</span>
                                    <FontAwesomeIcon icon={faChevronRight} />
                                </button>
                            </div>
                        </div>
                    )}
                </>
            )}

            {/* View Modal */}
            {showViewModal && selectedSupplier && (
                <div className="ozlistui-modal-overlay" onClick={(e) => e.stopPropagation()}>
                    <div className="ozlistui-modal-content ozlistui-view-modal-content" onClick={(e) => e.stopPropagation()} style={{ direction: isRTL ? 'rtl' : 'ltr' }}>
                        <div className="ozlistui-modal-header">
                            <div className="ozlistui-modal-icon"><FontAwesomeIcon icon={faUser} /></div>
                            <div className="ozlistui-modal-title">
                                <h3>{t.supplierDetails}</h3>
                                <span className="ozlistui-supplier-number-badge">#{selectedSupplier.supplierid}</span>
                            </div>
                            <button className="ozlistui-close-btn" onClick={() => setShowViewModal(false)}>
                                <FontAwesomeIcon icon={faTimes} />
                            </button>
                        </div>
                        <div className="ozlistui-modal-body ozlistui-view-modal-body">
                            <div className="ozlistui-view-section">
                                <h4 className="ozlistui-view-section-title"><FontAwesomeIcon icon={faUser} />{t.supplierSummary}</h4>
                                <div className="ozlistui-view-grid">
                                    <div className="ozlistui-view-row"><span className="ozlistui-view-label">{t.supplierName}:</span><span className="ozlistui-view-value" dir={isRTL ? 'rtl' : 'ltr'}>{selectedSupplier.supplier_name || '-'}</span></div>
                                    <div className="ozlistui-view-row"><span className="ozlistui-view-label">{t.company}:</span><span className="ozlistui-view-value" dir={isRTL ? 'rtl' : 'ltr'}>{selectedSupplier.company || '-'}</span></div>
                                    <div className="ozlistui-view-row"><span className="ozlistui-view-label">{t.category}:</span><span className="ozlistui-view-value">{selectedSupplier.category ? getCategoryLabel(selectedSupplier.category) : '-'}</span></div>
                                    <div className="ozlistui-view-row"><span className="ozlistui-view-label">{t.date}:</span><span className="ozlistui-view-value" dir={isRTL ? 'rtl' : 'ltr'}>{formatDate(selectedSupplier.date) || '-'}</span></div>
                                    {selectedSupplier.website && (
                                        <div className="ozlistui-view-row"><span className="ozlistui-view-label">{t.website}:</span><span className="ozlistui-view-value" dir="ltr">{selectedSupplier.website}</span></div>
                                    )}
                                    {selectedSupplier.contact_person && (
                                        <div className="ozlistui-view-row"><span className="ozlistui-view-label">{t.contactPerson}:</span><span className="ozlistui-view-value" dir={isRTL ? 'rtl' : 'ltr'}>{selectedSupplier.contact_person}</span></div>
                                    )}
                                </div>
                            </div>
                            <div className="ozlistui-view-section">
                                <h4 className="ozlistui-view-section-title"><FontAwesomeIcon icon={faPhone} />{t.contactInfo}</h4>
                                <div className="ozlistui-view-grid">
                                    <div className="ozlistui-view-row"><span className="ozlistui-view-label">{t.phone}:</span><span className="ozlistui-view-value" dir="ltr">{selectedSupplier.phone || '-'}</span></div>
                                    <div className="ozlistui-view-row"><span className="ozlistui-view-label">{t.email}:</span><span className="ozlistui-view-value" dir="ltr">{selectedSupplier.email || '-'}</span></div>
                                </div>
                            </div>
                            {selectedSupplier.address && (
                                <div className="ozlistui-view-section">
                                    <h4 className="ozlistui-view-section-title"><FontAwesomeIcon icon={faMapMarkerAlt} />{t.addressInfo}</h4>
                                    <div className="ozlistui-remarks-box" dir={isRTL ? 'rtl' : 'ltr'}>{selectedSupplier.address}</div>
                                </div>
                            )}
                            {selectedSupplier.notes && (
                                <div className="ozlistui-view-section">
                                    <h4 className="ozlistui-view-section-title"><FontAwesomeIcon icon={faStickyNote} />{t.notes}</h4>
                                    <div className="ozlistui-remarks-box" dir={isRTL ? 'rtl' : 'ltr'}>{selectedSupplier.notes}</div>
                                </div>
                            )}
                            <div className="ozlistui-view-section ozlistui-metadata-section">
                                <div className="ozlistui-view-grid">
                                    <div className="ozlistui-view-row"><span className="ozlistui-view-label">{t.addedBy}:</span><span className="ozlistui-view-value"><span className="ozlistui-added-by-badge" dir={isRTL ? 'rtl' : 'ltr'}>{selectedSupplier.username || '-'}</span></span></div>
                                    {selectedSupplier.created_at && (
                                        <div className="ozlistui-view-row"><span className="ozlistui-view-label">{t.dateAdded}:</span><span className="ozlistui-view-value" dir={isRTL ? 'rtl' : 'ltr'}>{formatDate(selectedSupplier.created_at)}</span></div>
                                    )}
                                    {selectedSupplier.updated_at && (
                                        <div className="ozlistui-view-row"><span className="ozlistui-view-label">{t.lastUpdated}:</span><span className="ozlistui-view-value" dir={isRTL ? 'rtl' : 'ltr'}>{formatDate(selectedSupplier.updated_at)}</span></div>
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className="ozlistui-modal-footer">
                            <button className="ozlistui-btn ozlistui-btn-secondary" onClick={() => setShowViewModal(false)}>{t.close}</button>
                            <button className="ozlistui-btn ozlistui-btn-primary" onClick={() => { setShowViewModal(false); handleEdit(selectedSupplier); }}>
                                <FontAwesomeIcon icon={faEdit} />
                                {t.edit}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Edit Modal */}
            {showEditModal && selectedSupplier && (
                <div className="ozlistui-modal-overlay" onClick={(e) => e.stopPropagation()}>
                    <div
                        ref={modalRef}
                        className="ozlistui-modal-content ozlistui-edit-modal-content"
                        onClick={(e) => e.stopPropagation()}
                        style={{
                            position: 'fixed',
                            left: modalPosition.x || '50%',
                            top: modalPosition.y || '50%',
                            transform: modalPosition.x === 0 && modalPosition.y === 0 ? 'translate(-50%, -50%)' : 'none',
                            width: modalSize.width,
                            minWidth: '350px',
                            maxWidth: '95vw',
                            maxHeight: '85vh',
                            direction: isRTL ? 'rtl' : 'ltr'
                        }}
                    >
                        <div className="ozlistui-resize-handle-top" onMouseDown={(e) => startResize(e, 'n')}></div>
                        <div className="ozlistui-resize-handle-bottom" onMouseDown={(e) => startResize(e, 's')}></div>
                        <div className="ozlistui-resize-handle-left" onMouseDown={(e) => startResize(e, 'w')}></div>
                        <div className="ozlistui-resize-handle-right" onMouseDown={(e) => startResize(e, 'e')}></div>
                        <div className="ozlistui-resize-handle-nw" onMouseDown={(e) => startResize(e, 'nw')}></div>
                        <div className="ozlistui-resize-handle-ne" onMouseDown={(e) => startResize(e, 'ne')}></div>
                        <div className="ozlistui-resize-handle-sw" onMouseDown={(e) => startResize(e, 'sw')}></div>
                        <div className="ozlistui-resize-handle-se" onMouseDown={(e) => startResize(e, 'se')}></div>

                        <div className="ozlistui-modal-header" onMouseDown={startDrag}>
                            <div className="ozlistui-modal-icon"><FontAwesomeIcon icon={faEdit} /></div>
                            <div className="ozlistui-modal-title">
                                <h3>{t.editSupplier}</h3>
                                <span className="ozlistui-supplier-number-badge">#{selectedSupplier.supplierid}</span>
                            </div>
                            <div className="ozlistui-width-control-group">
                                {Object.entries(widthModes).map(([key, mode]) => (
                                    <button key={key} className={`ozlistui-width-btn ${currentWidthMode === key ? 'ozlistui-active' : ''}`} onClick={() => setWidthMode(key)} title={mode.label}>
                                        <FontAwesomeIcon icon={mode.icon} />
                                    </button>
                                ))}
                            </div>
                            <button className="ozlistui-close-btn" onClick={() => setShowEditModal(false)}>
                                <FontAwesomeIcon icon={faTimes} />
                            </button>
                        </div>

                        <form onSubmit={handleUpdateSupplier}>
                            <div className="ozlistui-modal-body">
                                <div className="ozlistui-form-grid">
                                    <div className={`ozlistui-form-group ${editFormErrors.supplier_name ? 'ozlistui-has-error' : ''}`}>
                                        <div className="ozlistui-input-wrapper">
                                            <input
                                                type="text"
                                                id="edit_supplier_name"
                                                name="supplier_name"
                                                value={editFormData.supplier_name}
                                                onChange={(e) => setEditFormData({ ...editFormData, supplier_name: e.target.value })}
                                                placeholder=" "
                                                className={editFormErrors.supplier_name ? 'ozlistui-error' : ''}
                                                dir={isRTL ? 'rtl' : 'ltr'}
                                            />
                                            <label htmlFor="edit_supplier_name">
                                                <FontAwesomeIcon icon={faUser} />
                                                {t.supplierName} <span className="ozlistui-required">*</span>
                                            </label>
                                        </div>
                                        {editFormErrors.supplier_name && (
                                            <div className="ozlistui-error-message">
                                                <FontAwesomeIcon icon={faExclamationTriangle} />
                                                {editFormErrors.supplier_name}
                                            </div>
                                        )}
                                    </div>

                                    <div className="ozlistui-form-group">
                                        <div className="ozlistui-input-wrapper">
                                            <input
                                                type="text"
                                                id="edit_company"
                                                name="company"
                                                value={editFormData.company}
                                                onChange={(e) => setEditFormData({ ...editFormData, company: e.target.value })}
                                                placeholder=" "
                                                dir={isRTL ? 'rtl' : 'ltr'}
                                            />
                                            <label htmlFor="edit_company">
                                                <FontAwesomeIcon icon={faStore} />
                                                {t.company}
                                                {!isRTL && t.optional && <span> ({t.optional})</span>}
                                            </label>
                                        </div>
                                    </div>

                                    <div className={`ozlistui-form-group ${editFormErrors.phone ? 'ozlistui-has-error' : ''}`}>
                                        <div className="ozlistui-input-wrapper">
                                            <input
                                                type="tel"
                                                id="edit_phone"
                                                name="phone"
                                                value={editFormData.phone}
                                                onChange={(e) => setEditFormData({ ...editFormData, phone: e.target.value })}
                                                placeholder=" "
                                                dir="ltr"
                                            />
                                            <label htmlFor="edit_phone">
                                                <FontAwesomeIcon icon={faPhone} />
                                                {t.phone}
                                                {!isRTL && t.optional && <span> ({t.optional})</span>}
                                            </label>
                                        </div>
                                        {editFormErrors.phone && (
                                            <div className="ozlistui-error-message">
                                                <FontAwesomeIcon icon={faExclamationTriangle} />
                                                {editFormErrors.phone}
                                            </div>
                                        )}
                                    </div>

                                    <div className={`ozlistui-form-group ${editFormErrors.email ? 'ozlistui-has-error' : ''}`}>
                                        <div className="ozlistui-input-wrapper">
                                            <input
                                                type="email"
                                                id="edit_email"
                                                name="email"
                                                value={editFormData.email}
                                                onChange={(e) => setEditFormData({ ...editFormData, email: e.target.value })}
                                                placeholder=" "
                                                dir="ltr"
                                            />
                                            <label htmlFor="edit_email">
                                                <FontAwesomeIcon icon={faEnvelope} />
                                                {t.email}
                                                {!isRTL && t.optional && <span> ({t.optional})</span>}
                                            </label>
                                        </div>
                                        {editFormErrors.email && (
                                            <div className="ozlistui-error-message">
                                                <FontAwesomeIcon icon={faExclamationTriangle} />
                                                {editFormErrors.email}
                                            </div>
                                        )}
                                    </div>

                                    <div className="ozlistui-form-group">
                                        <div className="ozlistui-input-wrapper">
                                            <input
                                                type="text"
                                                id="edit_website"
                                                name="website"
                                                value={editFormData.website}
                                                onChange={(e) => setEditFormData({ ...editFormData, website: e.target.value })}
                                                placeholder=" "
                                                dir="ltr"
                                            />
                                            <label htmlFor="edit_website">
                                                <FontAwesomeIcon icon={faGlobe} />
                                                {t.website}
                                                {!isRTL && t.optional && <span> ({t.optional})</span>}
                                            </label>
                                        </div>
                                    </div>

                                    <div className="ozlistui-form-group">
                                        <div className="ozlistui-input-wrapper">
                                            <input
                                                type="text"
                                                id="edit_contact_person"
                                                name="contact_person"
                                                value={editFormData.contact_person}
                                                onChange={(e) => setEditFormData({ ...editFormData, contact_person: e.target.value })}
                                                placeholder=" "
                                                dir={isRTL ? 'rtl' : 'ltr'}
                                            />
                                            <label htmlFor="edit_contact_person">
                                                <FontAwesomeIcon icon={faUserTag} />
                                                {t.contactPerson}
                                                {!isRTL && t.optional && <span> ({t.optional})</span>}
                                            </label>
                                        </div>
                                    </div>

                                    <div className="ozlistui-form-group">
                                        <div className="ozlistui-input-wrapper">
                                            <input
                                                type="date"
                                                id="edit_date"
                                                name="date"
                                                value={editFormData.date}
                                                onChange={(e) => setEditFormData({ ...editFormData, date: e.target.value })}
                                                max={getTodayDate()}
                                                placeholder=" "
                                                dir={isRTL ? 'rtl' : 'ltr'}
                                            />
                                            <label htmlFor="edit_date">
                                                <FontAwesomeIcon icon={faCalendar} />
                                                {t.date}
                                            </label>
                                        </div>
                                    </div>

                                    <div className="ozlistui-form-group">
                                        <div className="ozlistui-input-wrapper">
                                            <select
                                                id="edit_category"
                                                name="category"
                                                value={editFormData.category}
                                                onChange={(e) => setEditFormData({ ...editFormData, category: e.target.value })}
                                                dir={isRTL ? 'rtl' : 'ltr'}
                                            >
                                                <option value="">{t.selectCategory}</option>
                                                {categoryOptions.filter(opt => opt.value).map(option => (
                                                    <option key={option.value} value={option.value}>{option.label}</option>
                                                ))}
                                            </select>
                                            <label htmlFor="edit_category">
                                                <FontAwesomeIcon icon={faTag} />
                                                {t.category}
                                                {!isRTL && t.optional && <span> ({t.optional})</span>}
                                            </label>
                                        </div>
                                    </div>

                                    <div className="ozlistui-form-group ozlistui-full-width">
                                        <div className="ozlistui-input-wrapper">
                                            <textarea
                                                id="edit_address"
                                                name="address"
                                                value={editFormData.address}
                                                onChange={(e) => setEditFormData({ ...editFormData, address: e.target.value })}
                                                rows="3"
                                                placeholder=" "
                                                dir={isRTL ? 'rtl' : 'ltr'}
                                            ></textarea>
                                            <label htmlFor="edit_address">
                                                <FontAwesomeIcon icon={faMapMarkerAlt} />
                                                {t.address}
                                                {!isRTL && t.optional && <span> ({t.optional})</span>}
                                            </label>
                                        </div>
                                    </div>

                                    <div className="ozlistui-form-group ozlistui-full-width">
                                        <div className="ozlistui-input-wrapper">
                                            <textarea
                                                id="edit_notes"
                                                name="notes"
                                                value={editFormData.notes}
                                                onChange={(e) => setEditFormData({ ...editFormData, notes: e.target.value })}
                                                rows="3"
                                                placeholder=" "
                                                dir={isRTL ? 'rtl' : 'ltr'}
                                            ></textarea>
                                            <label htmlFor="edit_notes">
                                                <FontAwesomeIcon icon={faStickyNote} />
                                                {t.notes}
                                                {!isRTL && t.optional && <span> ({t.optional})</span>}
                                            </label>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="ozlistui-modal-footer">
                                <button type="button" className="ozlistui-btn ozlistui-btn-secondary" onClick={() => setShowEditModal(false)}>
                                    <FontAwesomeIcon icon={faTimes} />
                                    {t.cancel}
                                </button>
                                <button type="submit" className="ozlistui-btn ozlistui-btn-primary" disabled={editLoading}>
                                    {editLoading ? (
                                        <>
                                            <div className="ozlistui-btn-spinner"></div>
                                            <span>{t.updating || 'Updating...'}</span>
                                        </>
                                    ) : (
                                        <>
                                            <FontAwesomeIcon icon={faSave} />
                                            <span>{t.saveChanges}</span>
                                        </>
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Delete Confirmation Modal */}
            {showDeleteModal && (
                <div className="ozlistui-modal-overlay" onClick={(e) => e.stopPropagation()}>
                    <div className="ozlistui-modal-content ozlistui-delete-modal" onClick={(e) => e.stopPropagation()} style={{ direction: isRTL ? 'rtl' : 'ltr' }}>
                        <div className="ozlistui-modal-header ozlistui-delete-header">
                            <div className="ozlistui-modal-icon"><FontAwesomeIcon icon={faExclamationTriangle} /></div>
                            <div className="ozlistui-modal-title"><h3>{t.confirmDelete}</h3></div>
                            <button className="ozlistui-close-btn" onClick={() => setShowDeleteModal(false)}><FontAwesomeIcon icon={faTimes} /></button>
                        </div>
                        <div className="ozlistui-modal-body">
                            <div className="ozlistui-delete-message">
                                <p>{t.deleteMessage}</p>
                                <p><FontAwesomeIcon icon={faExclamationTriangle} /> {t.deleteWarning}</p>
                            </div>
                        </div>
                        <div className="ozlistui-modal-footer">
                            <button type="button" className="ozlistui-btn ozlistui-btn-secondary" onClick={() => setShowDeleteModal(false)}><FontAwesomeIcon icon={faTimes} />{t.noCancel}</button>
                            <button type="button" className="ozlistui-btn ozlistui-btn-delete" onClick={handleDelete}><FontAwesomeIcon icon={faTrash} />{t.yesDelete}</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SupplierList;